import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import { useTranslation } from "react-i18next";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { mapboxAccessToken, mapInitialView, mapStyle, mapboxSatelliteStyle } from "./config";
import {
    ensureAddressesLayer,
    ensureAssignmentsLayer,
    ensureRoadsLayer,
    updateAddressesLayer,
    updateAddressAggregationsLayer,
    removeAddressAggregationsLayer,
    updateAssignmentsLayer,
    updateRoadsLayer,
    highlightRoadSegment,
    removeRoadSegmentHighlight,
    ROADS_MIN_ZOOM,
    updateAddressStatus,
    setAddressDeliveryOverride,
    markAddressAsUndelivered,
    applyAllCampaignsAddressVisibilityFilter,
    applyDeliveredOnlyAddressVisibilityFilter,
    ensurePostalCodesLayer,
    updatePostalCodesLayer,
    setPostalCodesLayerVisibility,
    setPostalCodesSelectionHighlight,
    ensureElectoralDistrictsLayer,
    updateElectoralDistrictsLayer,
    setElectoralDistrictsLayerVisibility,
    setElectoralDistrictsSelectionHighlight,
    setElectoralDistrictsMetadataPaint,
    ensureMunicipalitiesLayer,
    updateMunicipalitiesLayer,
    setMunicipalitiesLayerVisibility,
    updateMunicipalityHighlight,
    ADDRESS_CACHE_TTL_MS
} from "./layers";
import { useKretsMemberLocations } from "../api/kretsMembers";
import { getCachedFeatures } from "./layers/utils";
import { getAddressStatus, HIDDEN_VISIBLE_FROM_ZOOM } from "./layers/AddressesLayer";
import type {
    GeoJsonFeature,
    MunicipalityFeatureProperties,
    AssignmentsResponse,
    AssignmentFeatureProperties
} from "../api/map/types";
import { hasValidAssignmentPolygon } from "../api/assignmentArea";
import { setAddressesLayerTelemetry } from "./layers/AddressesLayer";
import {
    useRoadFeatures,
    useZoomAwareAddresses,
    useAssignmentPolygons,
    useMapViewportListener,
    requestMapDeliveriesFullFetch,
    removeAddressFlyerDeliveriesFromMapCache,
    addDeliveryToMapCache,
    hasMapCampaignScope,
    isAllCampaignsMode,
    isCampaignSelected,
    isAllCampaignsVisibleAddress,
    AGGREGATION_ZOOM_THRESHOLD
} from "../api/map";
import { parseBoundingBox } from "../api/map/utils";
import {
    MapLegend,
    MapStatusIndicator,
    MapFilterPanel,
    MapErrorAlert,
    MapLoadingSkeleton,
    MapZoomIndicator,
    MapStatusBar,
    RoReportingModal,
    PosterContextMenu,
    PosterPlaceDialog,
    PosterStatusBar,
    type Campaign,
    type FlyerType,
    type MapMode,
    type HouseholdStats
} from "./components";
import {
    fetchPostersGeoJson,
    placePoster as apiPlacePoster,
    deletePoster as apiDeletePoster,
    updatePosterStatus as apiUpdatePosterStatus
} from "../api/posters";
import type { PosterFeatureProperties } from "../api/posters";
import {
    ensurePostersLayer,
    updatePostersLayer,
    setPostersSelectedId,
    setPostersShowTakenDown,
    removePostersLayer,
    POSTERS_CLICKABLE_LAYER_ID
} from "./layers";
import { MissedAddressesModal } from "./components/MissedAddressesModal";
import { BulkDeliveryConfirmationDialog } from "./components/BulkDeliveryConfirmationDialog";
import { BulkAddressDeliveryPanel } from "./components/BulkAddressDeliveryPanel";
import { AddressContextMenu } from "./components/AddressContextMenu";
import { AddressDeliveryHistoryModal } from "./components/AddressDeliveryHistoryModal";
import { AddressEditDialog } from "./components/AddressEditDialog";
import { CreateAddressPopover } from "./components/CreateAddressPopover";
import { useTelemetry } from "../shared/useTelemetry";
import { useMapContext } from "./MapContext";
import {
    applyAdminAssignmentHighlightToMap,
    removeAdminAssignmentHighlightFromMap
} from "./adminAssignmentMapHighlight";
import {
    updateAdminAssignmentsOverviewLayer,
    updateAdminAssignmentLabelsLayer,
    removeAdminAssignmentsOverviewFromMap
} from "./adminAssignmentMapOverview";
import { runWhenStyleReady } from "./mapStyleReady";
import { PolygonDrawer } from "../assignments/PolygonDrawer";
import {
    useRecordDeliveriesByRoad,
    useRecordDeliveriesByAddresses,
    previewAddressCountByRoad,
    useDeliveriesByAddress,
    useDeliveriesByAddressAll,
    useDeleteDelivery,
    deliveryMatchesSelectedFlyerType,
    type DeliveryResponse
} from "../api/deliveries";
import { fetchFlyerTypes } from "../api/campaigns";
import { getCurrentSessionId } from "./utils/sessionManager";
import { getCurrentUser } from "../api/auth";
import { useAddress, useUpdateAddress, useCreateAddress } from "../api/addresses/hooks";
import { useMovePoint } from "./hooks/useMovePoint";
import { useBoxSelect } from "./hooks/useBoxSelect";
import toast from "react-hot-toast";
import type { RoadFeatureProperties } from "../api/map/types";
import type { AddressFeatureProperties } from "../api/map/types";
import type { AddressResponse } from "../api/addresses/types";
import "./map.css";

type MapErrorKey = "map.errors.noToken" | "map.errors.loadFailed";

export interface MapViewProps {
    campaignId?: string; // GUID - TODO: Get from context/store
    municipalityId?: string | null;
    flyerTypeId?: string | null; // GUID - Optional filter
    campaigns?: Campaign[];
    flyerTypes?: FlyerType[];
    onCampaignChange?: (campaignId: string) => void;
    onFlyerTypeChange?: (flyerTypeId: string | null) => void;
    showLegend?: boolean;
    showStatusIndicator?: boolean;
    showFilterPanel?: boolean;
    showStatusBar?: boolean;
    onMapReady?: (map: mapboxgl.Map) => void;
    hideAllLayers?: boolean; // If true, hide all data layers (addresses, roads, assignments) - just show the map
    showAssignments?: boolean;
    onAssignmentsDataChange?: (data: AssignmentsResponse | null) => void;
    showPostalCodes?: boolean;
    showElectoralDistricts?: boolean;
    showMunicipalities?: boolean;
    postalCodesData?: import("../api/map/types").PostalCodesResponse | null;
    electoralDistrictsData?: import("../api/map/types").ElectoralDistrictsResponse | null;
    municipalitiesData?: import("../api/map/types").MunicipalitiesResponse | null;
    onViewportBboxChange?: (bbox: string | undefined) => void;
    selectedPostalCodeIds?: string[];
    selectedElectoralDistrictIds?: string[];
    onTogglePostalCode?: (postalCodePolygonId: string) => void;
    onTogglePriorityElectoralDistrict?: (electoralDistrictId: string) => void;
    /** When true, click on postal code polygons toggles selection. Controlled from Kartlager. */
    clickPostalCodesEnabled?: boolean;
    /** When true, click on electoral district polygons toggles selection. Controlled from Kartlager. */
    clickElectoralDistrictsEnabled?: boolean;
    /** When set, map fits to this bbox (e.g. when municipality or saved set is loaded). Format: "minLon,minLat,maxLon,maxLat" */
    fitBounds?: string | null;
    /** When set, the selected municipality is outlined (no fill) on the map. */
    selectedMunicipalityId?: string | null;
    /** GeoJSON feature for the selected municipality (from list). When set, a clear outline is drawn. */
    selectedMunicipalityFeature?: GeoJsonFeature<MunicipalityFeatureProperties> | null;
    /** Map electoralDistrictId -> number for choropleth coloring (when a metadata field is selected). */
    electoralMetadataValues?: Record<string, number>;
    /** When set, fill uses green scale by metadataValue; otherwise default purple. */
    electoralMetadataFieldId?: string | null;
    /** Min/max for the metadata color scale. When null, default purple fill is used (e.g. to compare with valda distrikt). */
    electoralMetadataMinMax?: { min: number; max: number } | null;
    /** Per-district tooltip data for map hover (name + metadata fields). When set, hovering electoral districts shows a tooltip. */
    electoralDistrictTooltipData?: import("../api/electoral-metadata/types").ElectoralDistrictTooltipDataMap | null;
    /** When true, shows orange pins for krets members who have a home address set. */
    showDistributorPins?: boolean;
    /** When set, distributor pins are filtered to this municipality. When null/undefined, all accessible pins are shown. */
    distributorPinsMunicipalityId?: string | null;
    /**
     * Kommunkod for the municipality actively picked in Kartlager. Only the overview modes
     * (all-campaigns and the delivery overview) clip to it — normal delivery work must keep
     * showing addresses across a municipality border, since the kommun is preselected from
     * the user's team rather than actively chosen.
     */
    municipalityCode?: string | null;
}

export const MapView = ({
    campaignId,
    flyerTypeId,
    campaigns = [],
    flyerTypes = [],
    onCampaignChange,
    onFlyerTypeChange,
    showLegend = true,
    showStatusIndicator = true,
    showFilterPanel = true,
    showStatusBar = true,
    onMapReady,
    hideAllLayers = false,
    showAssignments = false,
    onAssignmentsDataChange,
    municipalityId = null,
    showPostalCodes = false,
    showElectoralDistricts = false,
    showMunicipalities = false,
    postalCodesData = null,
    electoralDistrictsData = null,
    municipalitiesData = null,
    onViewportBboxChange,
    selectedPostalCodeIds = [],
    selectedElectoralDistrictIds = [],
    onTogglePostalCode,
    onTogglePriorityElectoralDistrict,
    clickPostalCodesEnabled = false,
    clickElectoralDistrictsEnabled = false,
    fitBounds = null,
    selectedMunicipalityId: _selectedMunicipalityId = null,
    selectedMunicipalityFeature = null,
    electoralMetadataValues,
    electoralMetadataFieldId = null,
    electoralMetadataMinMax = { min: 0, max: 100 },
    electoralDistrictTooltipData = null,
    showDistributorPins = false,
    distributorPinsMunicipalityId = null,
    municipalityCode = null
}: MapViewProps): JSX.Element => {
    const { t } = useTranslation();
    const telemetry = useTelemetry();
    const queryClient = useQueryClient();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const distributorMarkersRef = useRef<mapboxgl.Marker[]>([]);
    const electoralTooltipPopupRef = useRef<mapboxgl.Popup | null>(null);
    const electoralTooltipDistrictIdRef = useRef<string | null>(null);
    const isInitializingRef = useRef(false);
    const [errorKey, setErrorKey] = useState<MapErrorKey | null>(null);
    const [viewportBbox, setViewportBbox] = useState<string | undefined>();
    const viewportBboxRef = useRef<string | undefined>(undefined);
    const [dismissedErrors, setDismissedErrors] = useState<Set<string>>(new Set());
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const isMapLoadedRef = useRef(false);
    const firstUserLocationRef = useRef(true); // Track if this is first user location update
    const [showRoReportingModal, setShowRoReportingModal] = useState(false);
    const [showMissedAddressesModal, setShowMissedAddressesModal] = useState(false);
    const [hasGpsPositionBeenSet, setHasGpsPositionBeenSet] = useState(false); // Track if GPS position has been set (for mobile app)
    const [isMobileApp, setIsMobileApp] = useState(false); // Track if we're in mobile app mode

    // Refs to access latest selection IDs inside async callbacks without adding them as effect dependencies
    const selectedPostalCodeIdsRef = useRef(selectedPostalCodeIds);
    selectedPostalCodeIdsRef.current = selectedPostalCodeIds;
    const selectedElectoralDistrictIdsRef = useRef(selectedElectoralDistrictIds);
    selectedElectoralDistrictIdsRef.current = selectedElectoralDistrictIds;
    const hideAllLayersRef = useRef(hideAllLayers);
    hideAllLayersRef.current = hideAllLayers;
    const flyerTypeIdRef = useRef(flyerTypeId);
    flyerTypeIdRef.current = flyerTypeId;
    const allCampaignsMode = isAllCampaignsMode(campaignId);

    // Get current user ID
    const { data: currentUser } = useQuery({
        queryKey: ["auth", "me"],
        queryFn: getCurrentUser,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: false
    });

    // Fetch campaign flyer types for bulk delivery dialog and color coding
    const { data: campaignFlyers = [] } = useQuery({
        queryKey: ["campaigns", campaignId, "flyer-types"],
        queryFn: () => fetchFlyerTypes(campaignId!),
        enabled: isCampaignSelected(campaignId),
        staleTime: 5 * 60 * 1000
    });

    const campaignFlyerTypes = useMemo(() => {
        const result = campaignFlyers
            .map((cf) => {
                // If it has a flyerType, use that
                if (cf.flyerType) {
                    return cf.flyerType;
                }
                // Otherwise, create a virtual FlyerType from CampaignFlyer properties
                // Use CampaignFlyer.id as the identifier for campaign-specific flyers
                return {
                    id: cf.id, // Use CampaignFlyer.id for campaign-specific flyers
                    name: cf.name,
                    summary: cf.summary,
                    imageUrl: cf.imageUrl
                } as FlyerType;
            })
            .filter((ft): ft is FlyerType => ft !== undefined);
        
        // Debug logging
        const campaignSpecific = result.filter(ft => campaignFlyers.some(cf => cf.id === ft.id && !cf.flyerType));
        if (campaignSpecific.length > 0) {
            console.log("[MapView] DEBUG - Campaign-specific flyers in campaignFlyerTypes:", 
                campaignSpecific.map(ft => ({ id: ft.id, name: ft.name }))
            );
        }
        
        return result;
    }, [campaignFlyers]);

    // Clear delivery status when campaign or flyer type changes
    const prevCampaignIdRef = useRef<string | undefined>(campaignId);
    const prevFlyerTypeIdRef = useRef<string | null | undefined>(flyerTypeId);
    useEffect(() => {
        const campaignChanged = prevCampaignIdRef.current !== campaignId;
        const flyerTypeChanged = prevFlyerTypeIdRef.current !== flyerTypeId;

        if ((campaignChanged || flyerTypeChanged) && mapRef.current && isMapLoaded) {
            // Clear delivery status from all addresses on the map
            const map = mapRef.current;
            const source = map.getSource("addresses");
            if (source && source.type === "geojson") {
                const geojsonSource = source as mapboxgl.GeoJSONSource;
                try {
                    const sourceData = (geojsonSource as any)._data as {
                        type: "FeatureCollection";
                        features: Array<GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>>
                    } | null;

                    if (sourceData && sourceData.features) {
                        // Clear flyerStatuses from all addresses to reset delivery status
                        const updatedFeatures = sourceData.features.map((feature) => {
                            const updatedProperties = {
                                ...feature.properties,
                                flyerStatuses: [] // Clear all delivery statuses
                            };

                            // Recalculate status - should now be "pending" (blue)
                            const updatedStatus = getAddressStatus(
                                updatedProperties,
                                flyerTypeId ?? undefined,
                                campaignFlyerTypes,
                                isAllCampaignsMode(campaignId)
                            );

                            return {
                                ...feature,
                                properties: {
                                    ...updatedProperties,
                                    status: updatedStatus,
                                    deliveredCount: 0,
                                    totalFlyerCount: 0
                                }
                            };
                        });

                        geojsonSource.setData({
                            type: "FeatureCollection",
                            features: updatedFeatures
                        } as any);
                    }
                } catch (error) {
                    console.warn("[MapView] Failed to clear delivery status:", error);
                }
            }

            if (campaignChanged) {
                setSelectedAddressIds([]);
                setAllCampaignsShowUndelivered(false);
                setNotDeliveredSinceDate(null);
                setDeliveredSinceDate(null);
                setDeliveredUntilDate(null);
            }

            // Invalidate deliveries queries to clear cached delivery data
            queryClient.invalidateQueries({ queryKey: ["deliveries"] });
        }

        prevCampaignIdRef.current = campaignId;
        prevFlyerTypeIdRef.current = flyerTypeId;
    }, [campaignId, flyerTypeId, isMapLoaded, queryClient, campaignFlyerTypes]);
    const [bulkDeliveryDialog, setBulkDeliveryDialog] = useState<{
        roadSegmentId: number;
        roadName?: string | null;
        estimatedAddressCount: number;
    } | null>(null);
    const [selectedAddressIds, setSelectedAddressIds] = useState<string[]>([]);
    const [isBoxSelectMode, setIsBoxSelectMode] = useState(false);
    const [allCampaignsShowUndelivered, setAllCampaignsShowUndelivered] = useState(false);
    // Narrows the all-campaigns undelivered view to addresses never delivered, or last
    // delivered before this date (YYYY-MM-DD). Only meaningful while the checkbox above is on.
    const [notDeliveredSinceDate, setNotDeliveredSinceDate] = useState<string | null>(null);
    // Opposite direction: only addresses last delivered on/after this date (YYYY-MM-DD).
    // Independent of the checkbox — works on the normally-shown delivered set.
    const [deliveredSinceDate, setDeliveredSinceDate] = useState<string | null>(null);
    // Optional upper bound for deliveredSinceDate, turning the open-ended "since" into a
    // closed window. Only meaningful together with deliveredSinceDate.
    const [deliveredUntilDate, setDeliveredUntilDate] = useState<string | null>(null);
    // Campaign/flyer view: whether never-delivered address points are drawn too. Off turns the
    // map into a delivery overview (delivered addresses only, no roads) — the counterpart of
    // the all-campaigns checkbox above. See deliveredOnlyView below.
    const [campaignShowUndelivered, setCampaignShowUndelivered] = useState(true);
    const selectedAddressIdsForLayerRef = useRef(selectedAddressIds);
    selectedAddressIdsForLayerRef.current = selectedAddressIds;
    const [currentZoom, setCurrentZoom] = useState<number>(12);
    /**
     * Delivery overview for the selected campaign/flyer: delivered addresses only, no roads,
     * scoped to the kommun picked in Kartlager instead of the viewport cap.
     *
     * Turned on either by unchecking "visa ej utdelade adresspunkter", or automatically once
     * the map is zoomed out past the point where individual points are normally fetched —
     * a whole kommun holds tens of thousands of address points, far more than a campaign
     * fetch is capped for, but the delivered subset of it loads fine. Without this the
     * zoomed-out campaign view fell back to the aggregation grid, which ignores the flyer
     * filter entirely and so showed nothing useful for a picked flyer.
     */
    const deliveredOnlyView =
        isCampaignSelected(campaignId) &&
        (!campaignShowUndelivered || currentZoom < AGGREGATION_ZOOM_THRESHOLD);
    const [useSatelliteImagery, setUseSatelliteImagery] = useState(false);
    const [mapStyleEpoch, setMapStyleEpoch] = useState(0);
    const [isBaseMapSwitching, setIsBaseMapSwitching] = useState(false);

    // Address editing state
    const [contextMenu, setContextMenu] = useState<{
        address: AddressResponse;
        position: { x: number; y: number };
    } | null>(null);
    const [deliveryHistoryAddress, setDeliveryHistoryAddress] = useState<AddressResponse | null>(null);
    const [editDialogAddressId, setEditDialogAddressId] = useState<string | null>(null);
    const [createPopover, setCreatePopover] = useState<{
        position: { x: number; y: number };
        latitude: number;
        longitude: number;
    } | null>(null);
    const [isCreateMode, setIsCreateMode] = useState(false);

    // Poster mode state
    const [mapMode, setMapMode] = useState<MapMode>("flyer");
    const mapModeRef = useRef<MapMode>("flyer");
    mapModeRef.current = mapMode;
    const [posterContextMenu, setPosterContextMenu] = useState<{
        poster: PosterFeatureProperties;
        position: { x: number; y: number };
    } | null>(null);
    const [posterPlaceDialog, setPosterPlaceDialog] = useState<{
        position: { x: number; y: number };
        latitude: number;
        longitude: number;
    } | null>(null);
    const [isPosterActionLoading, setIsPosterActionLoading] = useState(false);
    // Nedtagna affischer är historik och döljs som standard, annars ser kartan
    // fullare ut än vad som faktiskt sitter uppe.
    const [showTakenDownPosters, setShowTakenDownPosters] = useState(false);
    const posterFetchAbortRef = useRef<AbortController | null>(null);
    // Flag to prevent map-level click from firing when a poster pin was clicked
    const posterPinClickedRef = useRef(false);

    // Address editing hooks
    const { data: editAddress } = useAddress(editDialogAddressId);
    const updateAddress = useUpdateAddress();
    const createAddress = useCreateAddress();
    const movePoint = useMovePoint(mapRef.current);

    // Get polygon drawing state and map instance from context
    const mapContext = useMapContext();
    const {
        isDrawingPolygon,
        polygonArea,
        setPolygonArea,
        map: contextMap,
        highlightedAssignmentArea,
        highlightedAssignmentId,
        assignmentHighlightNonce
    } = mapContext;

    // Close create address mode when starting to draw polygon for assignments
    useEffect(() => {
        if (isDrawingPolygon && isCreateMode) {
            setIsCreateMode(false);
            setCreatePopover(null);
        }
    }, [isDrawingPolygon, isCreateMode]);

    // Detect mobile app mode via body class
    useEffect(() => {
        const checkMobileApp = () => {
            setIsMobileApp(document.body.classList.contains("mobile-app-active"));
        };
        
        // Check immediately
        checkMobileApp();
        
        // Observe changes to body class
        const observer = new MutationObserver(checkMobileApp);
        observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
        
        return () => observer.disconnect();
    }, []);

    // Bulk delivery mutation
    const recordDeliveriesByRoadMutation = useRecordDeliveriesByRoad();
    const recordDeliveriesByAddressesMutation = useRecordDeliveriesByAddresses();
    const deleteDeliveryMutation = useDeleteDelivery();

    // Get deliveries for the address in context menu
    const deliveriesQuery = useDeliveriesByAddress(
        contextMenu && isCampaignSelected(campaignId) ? contextMenu.address.id : undefined,
        isCampaignSelected(campaignId) ? campaignId : undefined
    );
    const deliveriesAllCampaignsQuery = useDeliveriesByAddressAll(
        contextMenu && allCampaignsMode ? contextMenu.address.id : undefined
    );
    const contextMenuDeliveries = allCampaignsMode
        ? deliveriesAllCampaignsQuery.data
        : deliveriesQuery.data;

    // Get deliveries for single selected address (for BulkAddressDeliveryPanel)
    const singleSelectedAddressId = selectedAddressIds.length === 1 ? selectedAddressIds[0] : undefined;
    const singleAddressDeliveriesQuery = useDeliveriesByAddress(
        singleSelectedAddressId,
        isCampaignSelected(campaignId) ? campaignId : undefined
    );

    // Get single address information when exactly one address is selected
    const singleAddress = useMemo(() => {
        if (selectedAddressIds.length !== 1 || !mapRef.current || !isMapLoaded) {
            return undefined;
        }

        const addressId = selectedAddressIds[0];
        const allAddresses = getCachedFeatures<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>(mapRef.current, "addresses");
        
        const feature = allAddresses.find((f) => {
            const id = f.id ?? f.properties.addressPointId;
            return id && String(id) === String(addressId);
        });

        if (!feature) {
            return undefined;
        }

        const properties = feature.properties;
        const geometry = feature.geometry as GeoJSON.Point;

        return {
            id: properties.addressPointId,
            street: properties.street,
            number: properties.number ?? null,
            apartment: properties.apartment ?? null,
            city: properties.city,
            postalCode: properties.postalCode,
            latitude: geometry.coordinates[1],
            longitude: geometry.coordinates[0],
            isBlocked: properties.isBlocked,
            isHidden: properties.isHidden,
            deliveryUnitCount: properties.deliveryUnitCount,
            createdAt: properties.createdAt,
            updatedAt: properties.updatedAt ?? null
        } as AddressResponse;
    }, [selectedAddressIds, isMapLoaded]);

    // Monitor online/offline status
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);

    // Helper function to move user location layer to top
    const moveUserLocationLayerToTop = useCallback((map: mapboxgl.Map, layerId: string) => {
        try {
            // Move layer to top (without beforeId parameter, it moves to top)
            if (map.getLayer(layerId)) {
                map.moveLayer(layerId);
            }
        } catch (error) {
            console.warn("[MapView] Failed to move user location layer to top:", error);
        }
    }, []);

    // Helper function to ensure user location layer exists
    const ensureUserLocationLayer = useCallback((map: mapboxgl.Map) => {
        if (!map.isStyleLoaded()) {
            map.once("style.load", () => {
                ensureUserLocationLayer(map);
            });
            return;
        }

        const SOURCE_ID = "user-location";
        const LAYER_ID = "user-location-dot";

        try {
            // Add source if it doesn't exist
            if (!map.getSource(SOURCE_ID)) {
                map.addSource(SOURCE_ID, {
                    type: "geojson",
                    data: { type: "FeatureCollection", features: [] }
                });
            }

            // Add layer if it doesn't exist
            if (!map.getLayer(LAYER_ID)) {
                map.addLayer({
                    id: LAYER_ID,
                    type: "circle",
                    source: SOURCE_ID,
                    paint: {
                        "circle-radius": 10, // Increased from 7 for better visibility
                        "circle-color": "#ef4444", // Red color for better contrast against blue address markers
                        "circle-stroke-width": 3, // Increased from 2 for better visibility
                        "circle-stroke-color": "#ffffff" // White stroke for contrast
                    }
                });

                // Always move layer to top so it's always visible
                moveUserLocationLayerToTop(map, LAYER_ID);
            } else {
                // Layer exists, ensure it's on top
                moveUserLocationLayerToTop(map, LAYER_ID);
            }
        } catch (error) {
            console.warn("[MapView] Failed to ensure user location layer:", error);
        }
    }, []);

    const handleBaseMapToggle = useCallback(() => {
        const map = mapRef.current;
        if (!map || !isMapLoadedRef.current || isBaseMapSwitching) {
            return;
        }

        const nextSatellite = !useSatelliteImagery;
        setIsBaseMapSwitching(true);
        try {
            map.setStyle(nextSatellite ? mapboxSatelliteStyle : mapStyle);
        } catch {
            setIsBaseMapSwitching(false);
            return;
        }

        map.once("style.load", () => {
            try {
                if (!hideAllLayersRef.current) {
                    ensureRoadsLayer(map);
                    ensureAddressesLayer({
                        map,
                        selectedFlyerTypeId: flyerTypeIdRef.current ?? null,
                        selectedAddressIds: selectedAddressIdsForLayerRef.current
                    });
                    ensureAssignmentsLayer(map);
                }
                ensureUserLocationLayer(map);
                setUseSatelliteImagery(nextSatellite);
                setMapStyleEpoch((n) => n + 1);
            } finally {
                setIsBaseMapSwitching(false);
            }
        });
    }, [useSatelliteImagery, isBaseMapSwitching, ensureUserLocationLayer]);

    // Initialize map
    useEffect(() => {
        if (mapRef.current || !containerRef.current || isInitializingRef.current) {
            return;
        }

        if (!mapboxAccessToken) {
            setErrorKey("map.errors.noToken");
            return;
        }

        isInitializingRef.current = true;

        // Set telemetry callback for addresses layer render timing
        setAddressesLayerTelemetry((eventName, payload) => {
            telemetry.track(eventName, payload);
        });

        mapboxgl.accessToken = mapboxAccessToken;

        try {
            const mapInstance = new mapboxgl.Map({
                container: containerRef.current,
                style: mapStyle,
                center: mapInitialView.center,
                zoom: mapInitialView.zoom,
                minZoom: 3,
                maxZoom: 18,
                attributionControl: true
            });

            // Track zoom level changes for zoom-aware data loading
            // Only use zoomend to avoid excessive re-renders during smooth zoom animations
            mapInstance.on("zoomend", () => {
                setCurrentZoom(mapInstance.getZoom());
            });

            // Set initial zoom
            setCurrentZoom(mapInstance.getZoom());

            mapInstance.addControl(
                new mapboxgl.NavigationControl({ visualizePitch: true }),
                "top-right"
            );
            mapInstance.addControl(new mapboxgl.ScaleControl(), "bottom-right");

            mapInstance.once("load", () => {
                if (!hideAllLayers) {
                    ensureRoadsLayer(mapInstance);
                    ensureAddressesLayer({ map: mapInstance, selectedFlyerTypeId: flyerTypeId, selectedAddressIds });
                    ensureAssignmentsLayer(mapInstance);
                }

                // Initialize user location layer for GPS tracking
                ensureUserLocationLayer(mapInstance);

                isMapLoadedRef.current = true;
                setIsMapLoaded(true);
                telemetry.track("map.loaded", {
                    style: mapStyle,
                    zoom: mapInitialView.zoom
                });
                onMapReady?.(mapInstance);
            });

            mapRef.current = mapInstance;
            
            // Expose map instance to window for mobile app access
            (window as any).map = mapInstance;
            
            setErrorKey(null);
        } catch (error) {
            telemetry.trackError("map.failed", error);
            setErrorKey("map.errors.loadFailed");
        }

        return () => {
            setAddressesLayerTelemetry(null);
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
            isMapLoadedRef.current = false;
            isInitializingRef.current = false;
            setIsMapLoaded(false);
            setViewportBbox(undefined);
            viewportBboxRef.current = undefined;
        };
    }, [telemetry]);

    // Listen to viewport changes (only when map is loaded)
    const handleViewportChange = useCallback((bbox: string) => {
        // Only update viewport if map is actually loaded (use ref for reliable check in closure)
        if (!isMapLoadedRef.current || !mapRef.current) {
            return;
        }
        if (viewportBboxRef.current === bbox) {
            return;
        }
        viewportBboxRef.current = bbox;
        setViewportBbox(bbox);
        onViewportBboxChange?.(bbox);
    }, [onViewportBboxChange]);

    // Disable viewport listener when drawing polygon to reduce background activity
    useMapViewportListener(mapRef.current, handleViewportChange, isDrawingPolygon ? 0 : undefined);

    // Set initial bounding box when map loads
    // CRITICAL: Don't check isStyleLoaded() as it may be false on Safari reload
    useEffect(() => {
        console.log("[MapView] Initial bbox check - mapRef:", !!mapRef.current, "isMapLoaded:", isMapLoaded, "viewportBbox:", viewportBbox ? "present" : "missing");

        if (mapRef.current && isMapLoaded && !viewportBbox) {
            const bounds = mapRef.current.getBounds();
            if (bounds) {
                const sw = bounds.getSouthWest();
                const ne = bounds.getNorthEast();
                const padding = 0.001;
                const initialBbox = `${sw.lng - padding},${sw.lat - padding},${ne.lng + padding},${ne.lat + padding}`;
                console.log("[MapView] Setting initial bbox:", initialBbox.substring(0, 50));
                viewportBboxRef.current = initialBbox;
                setViewportBbox(initialBbox);

                // Log to native if available
                if (typeof (window as any).cfDebugLog === 'function') {
                    (window as any).cfDebugLog(`Initial bbox set: ${initialBbox.substring(0, 40)}`);
                }
            } else {
                console.warn("[MapView] Could not get map bounds - map not ready?");
            }
        }
    }, [isMapLoaded, viewportBbox]);

    useEffect(() => {
        viewportBboxRef.current = viewportBbox;
    }, [viewportBbox]);

    // Mobile bridge: Expose functions for native app to call
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded) {
            return;
        }

        const map = mapRef.current;

        // Ensure user location layer is always on top when new layers are added
        const ensureLayerOnTop = () => {
            if (map.getLayer("user-location-dot")) {
                moveUserLocationLayerToTop(map, "user-location-dot");
            }
        };

        // Handler for data events
        const handleDataEvent = (e: any) => {
            // When new data is loaded (new layers might be added), ensure user location is on top
            if (e.dataType === "style") {
                ensureLayerOnTop();
            }
        };

        // Listen for style changes and layer additions to keep user location on top
        map.on("style.load", ensureLayerOnTop);
        map.on("data", handleDataEvent);

        // Store references for cleanup
        const styleLoadHandler = ensureLayerOnTop;
        const dataEventHandler = handleDataEvent;

        // Track pan throttling
        let lastPanTime = 0;
        const PAN_THROTTLE_MS = 1000; // Max 1 pan per second

        // Expose functions to native app
        (window as any).cfSetUserLocation = (lat: number, lon: number, fly: boolean = false, autoFollow: boolean = false) => {
            if (!map || !map.isStyleLoaded()) {
                return;
            }

            // Mark that GPS position has been set (for mobile app - enables data loading)
            setHasGpsPositionBeenSet(true);

            // Ensure layer exists and is on top
            ensureUserLocationLayer(map);
            moveUserLocationLayerToTop(map, "user-location-dot");

            // Update the user location marker
            const source = map.getSource("user-location");
            if (source && source.type === "geojson") {
                try {
                    (source as mapboxgl.GeoJSONSource).setData({
                        type: "FeatureCollection",
                        features: [{
                            type: "Feature",
                            geometry: {
                                type: "Point",
                                coordinates: [lon, lat]
                            },
                            properties: {}
                        }]
                    });
                } catch (error) {
                    console.warn("[MapView] Failed to update user location:", error);
                }
            }

            // Ensure layer is still on top after update
            moveUserLocationLayerToTop(map, "user-location-dot");

            // Handle map movement
            if (autoFollow) {
                // Auto-follow mode: use smooth panTo for continuous following
                const now = Date.now();
                if (now - lastPanTime >= PAN_THROTTLE_MS) {
                    lastPanTime = now;
                    try {
                        map.panTo([lon, lat], {
                            duration: 500 // Smooth 500ms transition
                        });
                    } catch (error) {
                        console.warn("[MapView] Failed to pan to user location:", error);
                    }
                }
            } else if (fly || firstUserLocationRef.current) {
                // Initial fly or manual fly
                firstUserLocationRef.current = false;
                try {
                    map.flyTo({
                        center: [lon, lat],
                        zoom: Math.max(map.getZoom(), 15)
                    });
                } catch (error) {
                    console.warn("[MapView] Failed to fly to user location:", error);
                }
            }
        };

        (window as any).cfReloadCampaign = () => {
            // Invalidate AND refetch ALL queries (active and inactive) to reload campaign data
            // CRITICAL: Use refetchType: 'all' to ensure ALL queries refetch, not just active ones
            // This fixes the Safari cache issue where reload wouldn't fetch new data
            console.log("[MapView] cfReloadCampaign called - invalidating and refetching ALL map queries");

            queryClient.invalidateQueries({
                queryKey: ["map"],
                refetchType: 'all' // Refetch ALL queries (active + inactive)
            });
            queryClient.invalidateQueries({
                queryKey: ["deliveries"],
                refetchType: 'all'
            });

            // Clear React Query cache completely for map queries to force fresh data
            queryClient.removeQueries({ queryKey: ["map"] });

            // Force immediate refetch of all map queries
            void queryClient.refetchQueries({
                queryKey: ["map"],
                type: 'all' // Ensure we refetch all, not just active
            });
        };

        // Expose function to reset viewport state for mobile app
        // This is needed when flyer type changes to reset lastBboxRef in useMapViewportListener
        (window as any).cfResetViewportState = () => {
            if (!map || !map.isStyleLoaded()) {
                console.log("[MapView] cfResetViewportState called but map not ready");
                return false;
            }

            console.log("[MapView] cfResetViewportState called - resetting viewport state");

            // Call the hook's reset function to clear lastBboxRef
            // This ensures that new pan events aren't ignored after flyer type changes
            if (typeof (window as any).__resetViewportLastBbox === 'function') {
                (window as any).__resetViewportLastBbox();
                console.log("[MapView] Viewport state reset complete");
            } else {
                console.warn("[MapView] __resetViewportLastBbox function not available");
            }

            return true;
        };

        (window as any).cfMarkAddressesAsDelivered = (addressIds: string[]) => {
            try {
                const source = map.getSource("addresses") as mapboxgl.GeoJSONSource;
                if (!source) {
                    console.warn("[MapView] Addresses source not found, cannot mark addresses as delivered");
                    return;
                }

                const data = source._data as GeoJSON.FeatureCollection;
                if (!data || !data.features) {
                    console.warn("[MapView] No address data found, cannot mark addresses as delivered");
                    return;
                }

                // Convert address IDs to strings for comparison (they might be Guids or strings)
                const addressIdSet = new Set(addressIds.map(id => String(id)));

                // Update status for matching addresses
                let updatedCount = 0;
                data.features.forEach((feature) => {
                    const featureId = feature.id ? String(feature.id) : feature.properties?.addressPointId ? String(feature.properties.addressPointId) : null;
                    if (featureId && addressIdSet.has(featureId)) {
                        // Update status to "delivered" to make it green
                        if (feature.properties) {
                            feature.properties.status = "delivered";
                            updatedCount++;
                        }
                    }
                });

                // Update the source with modified data
                source.setData(data);

                console.log(`[MapView] Marked ${updatedCount} addresses as delivered on map`);
            } catch (error) {
                console.error("[MapView] Error marking addresses as delivered:", error);
            }
        };

        // Debug logging bridge for mobile app
        (window as any).cfDebugLog = (message: string) => {
            console.log(`[WEBVIEW-DEBUG] ${message}`);
        };

        // NOTE: cfUpdateCampaign is defined in Shell.tsx which uses React Router's setSearchParams
        // to properly trigger state updates. Don't define it here.

        // Expose function to trigger viewport update after campaign change
        // This forces the map to recalculate the current viewport and trigger data loading
        (window as any).cfTriggerViewportUpdate = () => {
            console.log("[MapView] cfTriggerViewportUpdate called");

            // Use the direct trigger function from useMapViewportListener
            // This bypasses Mapbox events and directly calls the viewport change callback
            if (typeof (window as any).__triggerViewportUpdate === 'function') {
                const result = (window as any).__triggerViewportUpdate();
                console.log("[MapView] cfTriggerViewportUpdate - __triggerViewportUpdate result:", result);
                return result;
            }

            // Fallback to firing moveend event if direct trigger is not available
            if (!map || !map.isStyleLoaded()) {
                console.log("[MapView] cfTriggerViewportUpdate - map not ready and no direct trigger");
                return false;
            }

            console.log("[MapView] cfTriggerViewportUpdate - falling back to moveend event");

            // Reset the lastBboxRef to force the viewport listener to accept this change
            if (typeof (window as any).__resetViewportLastBbox === 'function') {
                (window as any).__resetViewportLastBbox();
            }

            try {
                map.fire("moveend");
                console.log("[MapView] cfTriggerViewportUpdate - fired moveend event");
                return true;
            } catch (error) {
                console.error("[MapView] cfTriggerViewportUpdate - error:", error);
                return false;
            }
        };

        // Cleanup
        return () => {
            map.off("style.load", styleLoadHandler);
            map.off("data", dataEventHandler);
            delete (window as any).cfSetUserLocation;
            delete (window as any).cfReloadCampaign;
            delete (window as any).cfMarkAddressesAsDelivered;
            delete (window as any).cfDebugLog;
            delete (window as any).cfResetViewportState;
            delete (window as any).cfTriggerViewportUpdate;
            delete (window as any).map;
        };
    }, [isMapLoaded, queryClient, ensureUserLocationLayer, moveUserLocationLayerToTop]);

    // Fetch roads data
    // Note: Don't use isStyleLoaded() in enabled condition - it can return false temporarily and disable queries
    // In mobile app, delay loading until GPS position has been set
    const roadsQuery = useRoadFeatures(
        {
            campaignId: campaignId ?? "",
            flyerTypeId: flyerTypeId ?? undefined,
            bbox: viewportBbox // Use viewportBbox for roads too
        },
        mapRef.current,
        {
            // Zoomed further out than ROADS_MIN_ZOOM the layer is hidden anyway (minzoom on
            // roads-line), so don't spend a request on geometry nobody can see.
            enabled: !hideAllLayers && isCampaignSelected(campaignId) && !deliveredOnlyView && currentZoom >= ROADS_MIN_ZOOM && !!mapRef.current && !!viewportBbox && isMapLoaded && (!isMobileApp || hasGpsPositionBeenSet),
            staleTime: 2 * 60 * 1000 // 2 minutes - reduced re-fetching
        }
    );

    // Phase 3.2: Use zoom-aware addresses hook (switches between aggregations and individual points)
    // Note: Don't use isStyleLoaded() in enabled condition - it can return false temporarily and disable queries
    // Disable address queries when drawing polygon to reduce background activity
    // In mobile app, delay loading until GPS position has been set
    const addressQueryEnabled = !hideAllLayers && !isDrawingPolygon && hasMapCampaignScope(campaignId) && !!mapRef.current && !!viewportBbox && isMapLoaded && (!isMobileApp || hasGpsPositionBeenSet);

    // Debug logging for address loading issues - CRITICAL for debugging Safari reload issue
    useEffect(() => {
        console.log("[MapView] Query conditions changed - addressQueryEnabled:", addressQueryEnabled,
            "hideAllLayers:", hideAllLayers,
            "campaignId:", campaignId,
            "mapRef:", !!mapRef.current,
            "viewportBbox:", viewportBbox?.substring(0, 30) || "MISSING",
            "isMapLoaded:", isMapLoaded,
            "currentZoom:", currentZoom);

        // Log to native if available
        if (typeof (window as any).cfDebugLog === 'function') {
            (window as any).cfDebugLog(`addressQueryEnabled=${addressQueryEnabled} campaignId=${campaignId || 'none'} bbox=${viewportBbox ? 'present' : 'MISSING'} isMapLoaded=${isMapLoaded}`);
        }
    }, [addressQueryEnabled, campaignId, viewportBbox, isMapLoaded, hideAllLayers, currentZoom]);

    const addressesQuery = useZoomAwareAddresses(
        {
            campaignId: campaignId ?? "",
            flyerTypeId: flyerTypeId ?? undefined,
            bbox: viewportBbox,
            includeUndelivered: allCampaignsMode && allCampaignsShowUndelivered,
            deliveredOnly: deliveredOnlyView && isCampaignSelected(campaignId),
            // Only the overview modes clip to the picked kommun — see the municipalityCode prop.
            municipalityCode:
                (allCampaignsMode || deliveredOnlyView) && municipalityCode ? municipalityCode : undefined
        },
        mapRef.current,
        {
            enabled: addressQueryEnabled,
            currentZoom: currentZoom // Pass explicit zoom level for reactivity
        }
    );

    // AddressesLayer keeps every address it has ever drawn for the current scope — merging
    // newly-fetched viewports into the map source instead of replacing it — so points don't
    // vanish while panning/zooming (see updateAddressesLayer in layers/AddressesLayer.ts).
    // The household counter below must accumulate the same way: reading only the latest
    // addressesQuery.data (one viewport's fetch) undercounts as soon as the map has drawn
    // more area than that single fetch covers, which is exactly what produced "16 of 986"
    // next to a screen full of yellow dots after panning into a fresh neighbourhood.
    // Each entry also carries a timestamp and expires after ADDRESS_CACHE_TTL_MS, mirroring
    // AddressesLayer's own expiry — otherwise an address cached once (say, before a delivery
    // was recorded there) would count as "not reached" forever unless the user happened to
    // pan back over that exact spot to refresh it.
    // Mutating a ref doesn't trigger a re-render, so a version counter forces the memo below
    // to recompute once new addresses have actually been merged in.
    const seenAddressesRef = useRef<Map<string, { properties: AddressFeatureProperties; cachedAt: number }>>(
        new Map()
    );
    const [seenAddressesVersion, setSeenAddressesVersion] = useState(0);
    useEffect(() => {
        seenAddressesRef.current = new Map();
        setSeenAddressesVersion((v) => v + 1);
        // Flyer and delivered-only both change which addresses the server returns at all, so
        // entries collected under the previous setting would skew the counter.
    }, [campaignId, flyerTypeId, municipalityCode, deliveredOnlyView]);
    useEffect(() => {
        // isZoomedOut means the response is the aggregation grid, not address features.
        if (!hasMapCampaignScope(campaignId) || addressesQuery.isZoomedOut) {
            return;
        }
        const data = addressesQuery.data as { features: { properties: AddressFeatureProperties }[] } | undefined;
        if (!data || data.features.length === 0) {
            return;
        }
        const cachedAt = Date.now();
        for (const feature of data.features) {
            seenAddressesRef.current.set(feature.properties.addressPointId, { properties: feature.properties, cachedAt });
        }
        setSeenAddressesVersion((v) => v + 1);
    }, [campaignId, addressesQuery.data, addressesQuery.isZoomedOut]);

    // Reached-households summary for the filter panel, in both the all-campaigns overview and
    // the campaign/flyer view. `total` is only meaningful once undelivered addresses are also
    // loaded (checkbox on) — the addresses query never sees the true denominator otherwise,
    // only the ones already delivered. Follows whichever date filter is active so the number
    // always matches what the map is currently showing, not the all-time total.
    const householdStats = useMemo((): HouseholdStats | null => {
        if (!hasMapCampaignScope(campaignId) || seenAddressesRef.current.size === 0) {
            return null;
        }
        const expiryThreshold = Date.now() - ADDRESS_CACHE_TTL_MS;
        let scopeTotal = 0;
        let scopeReached = 0;
        let matchedTotal = 0;
        let matchedReached = 0;
        for (const [id, entry] of seenAddressesRef.current) {
            if (entry.cachedAt < expiryThreshold) {
                // Stale — hasn't been refreshed since we last fetched its viewport. Drop it so
                // it stops counting toward a total it may no longer belong to.
                seenAddressesRef.current.delete(id);
                continue;
            }
            const p = entry.properties;
            if (p.isBlocked || p.isHidden) {
                continue;
            }
            const units = p.deliveryUnitCount > 0 ? p.deliveryUnitCount : 1;
            const hasDelivery = (p.flyerStatuses ?? []).some((fs) => fs.deliveredQuantity > 0 || fs.isNoThanks);
            scopeTotal += units;
            if (hasDelivery) {
                scopeReached += units;
            }
            if (
                isAllCampaignsVisibleAddress(
                    p,
                    allCampaignsShowUndelivered,
                    notDeliveredSinceDate,
                    deliveredSinceDate,
                    deliveredUntilDate
                )
            ) {
                matchedTotal += units;
                if (hasDelivery) {
                    matchedReached += units;
                }
            }
        }
        if (!allCampaignsMode) {
            // Campaign/flyer view: no date filters here, so every non-blocked address in the
            // scope counts, and the denominator is only known while undelivered points are
            // being fetched (checkbox on and not forced into the zoomed-out overview).
            return { mode: "all", count: scopeReached, total: deliveredOnlyView ? null : scopeTotal };
        }

        const total = allCampaignsShowUndelivered ? scopeTotal : null;
        if (deliveredSinceDate) {
            // Every match has a delivery by construction (isAllCampaignsVisibleAddress excludes
            // never-delivered addresses when deliveredSince is set).
            return deliveredUntilDate
                ? {
                      mode: "deliveredBetween",
                      count: matchedReached,
                      total,
                      since: deliveredSinceDate,
                      until: deliveredUntilDate
                  }
                : { mode: "deliveredSince", count: matchedReached, total, since: deliveredSinceDate };
        }
        if (notDeliveredSinceDate) {
            return { mode: "notDeliveredSince", count: matchedTotal, total, since: notDeliveredSinceDate };
        }
        return { mode: "all", count: matchedReached, total };
    }, [
        campaignId,
        allCampaignsMode,
        deliveredOnlyView,
        seenAddressesVersion,
        allCampaignsShowUndelivered,
        notDeliveredSinceDate,
        deliveredSinceDate,
        deliveredUntilDate
    ]);

    // Immediately prune cached undelivered points when the all-campaigns filter is turned off
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || !allCampaignsMode) {
            return;
        }
        applyAllCampaignsAddressVisibilityFilter(
            mapRef.current,
            allCampaignsShowUndelivered,
            notDeliveredSinceDate,
            deliveredSinceDate,
            deliveredUntilDate
        );
    }, [
        allCampaignsShowUndelivered,
        notDeliveredSinceDate,
        deliveredSinceDate,
        deliveredUntilDate,
        allCampaignsMode,
        isMapLoaded,
        mapStyleEpoch
    ]);

    // Same for the delivery overview: the layer merges each response into the points already
    // drawn, so without this the undelivered ones stay on screen until they scroll out.
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || !deliveredOnlyView || !isCampaignSelected(campaignId)) {
            return;
        }
        applyDeliveredOnlyAddressVisibilityFilter(mapRef.current, flyerTypeId ?? null, campaignFlyerTypes);
    }, [deliveredOnlyView, flyerTypeId, campaignId, campaignFlyerTypes, isMapLoaded, mapStyleEpoch]);

    // Fetch assignments data
    // Note: Don't use isStyleLoaded() in enabled condition - it can return false temporarily and disable queries
    const assignmentsQueryEnabledForMainMap =
        !hideAllLayers &&
        showAssignments &&
        (isCampaignSelected(campaignId) || !!municipalityId) &&
        !!mapRef.current &&
        !!viewportBbox &&
        isMapLoaded;
    // Admin overview: fetch every assignment in the municipality with no viewport clipping.
    // Clipping made the result depend on where the map happened to be panned while the
    // municipality geometry (fitBounds) was still loading, so assignments outside that
    // transient view silently disappeared. The query is already scoped to one municipality.
    const assignmentsQueryEnabledForAdminOverview = hideAllLayers && !!municipalityId;

    const assignmentsQuery = useAssignmentPolygons(
        {
            campaignId: campaignId || undefined,
            municipalityId: municipalityId ?? undefined,
            flyerTypeId: flyerTypeId ?? undefined,
            bbox: hideAllLayers ? undefined : viewportBbox
        },
        mapRef.current,
        {
            enabled: assignmentsQueryEnabledForMainMap || assignmentsQueryEnabledForAdminOverview,
            unbounded: assignmentsQueryEnabledForAdminOverview,
            staleTime: 3 * 60 * 1000 // 3 minutes - assignments change less frequently
        }
    );

    // All drawable assignment polygons in the municipality — used as-is for the name labels.
    const adminAssignmentPolygonFeatures = useMemo((): GeoJsonFeature<AssignmentFeatureProperties>[] => {
        const data = assignmentsQuery.data;
        if (!hideAllLayers || !data?.features?.length) {
            return [];
        }
        return data.features.filter((f) => {
            if (f.geometry?.type !== "Polygon") {
                return false;
            }
            const coords = (f.geometry as { coordinates?: number[][][] }).coordinates;
            if (!coords) {
                return false;
            }
            if (!f.properties?.hasCustomArea) {
                return false;
            }
            return hasValidAssignmentPolygon({ coordinates: coords });
        }) as GeoJsonFeature<AssignmentFeatureProperties>[];
    }, [hideAllLayers, assignmentsQuery.data]);

    // The highlighted assignment is drawn by the green highlight layer instead of the faint fill.
    const adminAssignmentsOverviewFeatures = useMemo((): GeoJsonFeature<AssignmentFeatureProperties>[] => {
        if (!highlightedAssignmentId) {
            return adminAssignmentPolygonFeatures;
        }
        return adminAssignmentPolygonFeatures.filter(
            (f) => String(f.properties.assignmentId) !== highlightedAssignmentId
        );
    }, [adminAssignmentPolygonFeatures, highlightedAssignmentId]);


    // Update layers when data changes (only when map is loaded)
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || hideAllLayers) {
            return;
        }

        // Clear roads layer when no specific campaign is selected (includes all-campaigns overview)
        // or when the delivery overview is on — that view is only about where we have been.
        if (!isCampaignSelected(campaignId) || deliveredOnlyView) {
            const source = mapRef.current.getSource("roads");
            if (source && source.type === "geojson") {
                (source as mapboxgl.GeoJSONSource).setData({ type: "FeatureCollection", features: [] });
            }
            return;
        }

        // Note: Don't check isStyleLoaded() here - it can return false temporarily and block layer updates
        // The isMapLoaded state is sufficient to know the map is ready

        if (roadsQuery.data) {
            updateRoadsLayer(mapRef.current, roadsQuery.data);
        } else if (roadsQuery.isError) {
            console.error("[MapView] Roads query error:", roadsQuery.error);
        } else if (!roadsQuery.isLoading && !roadsQuery.data) {
            // Clear roads if query is not loading and no data (campaign changed or disabled)
            const source = mapRef.current.getSource("roads");
            if (source && source.type === "geojson") {
                (source as mapboxgl.GeoJSONSource).setData({ type: "FeatureCollection", features: [] });
            }
        }
    }, [roadsQuery.data, roadsQuery.isError, roadsQuery.isLoading, roadsQuery.error, isMapLoaded, campaignId, hideAllLayers, deliveredOnlyView, mapStyleEpoch]);

    // Phase 3.2 & 3.3: Handle zoom-aware addresses (aggregations when zoomed out, individual points when zoomed in)
    // NOTE: isFetching and isLoading are intentionally NOT in the dependency array.
    // Background refetches (isFetching=true) must not trigger this effect while data is unchanged,
    // because updateAddressesLayer would overwrite optimistic Mapbox updates (mark delivered / undo)
    // with stale server data. The effect only needs to fire when the actual data changes.
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded) {
            return;
        }

        // Clear addresses layers if no campaign scope or hideAllLayers is true
        if (!hasMapCampaignScope(campaignId) || hideAllLayers) {
            const source = mapRef.current.getSource("addresses");
            if (source && source.type === "geojson") {
                (source as mapboxgl.GeoJSONSource).setData({ type: "FeatureCollection", features: [] });
            }
            removeAddressAggregationsLayer(mapRef.current);
            return;
        }

        // Phase 3.2: Switch between aggregations and individual points based on zoom
        if (addressesQuery.isZoomedOut) {
            // Always clear individual address circles when in aggregations mode,
            // regardless of whether aggregations data has arrived yet.
            const addressesLayerId = "addresses-points";
            if (mapRef.current.getLayer(addressesLayerId)) {
                mapRef.current.setLayoutProperty(addressesLayerId, "visibility", "none");
            }
            const addrSource = mapRef.current.getSource("addresses");
            if (addrSource && addrSource.type === "geojson") {
                (addrSource as mapboxgl.GeoJSONSource).setData({ type: "FeatureCollection", features: [] });
            }

            if (addressesQuery.data) {
                updateAddressAggregationsLayer(mapRef.current, addressesQuery.data as any, currentZoom);
            } else if (addressesQuery.isError) {
                console.error("[MapView] Address aggregations query error:", addressesQuery.error);
                removeAddressAggregationsLayer(mapRef.current);
            } else {
                removeAddressAggregationsLayer(mapRef.current);
            }
        } else {
            // Show individual address points (zoom >= aggregation threshold)
            if (addressesQuery.data) {
                const addressesLayerId = "addresses-points";
                if (mapRef.current.getLayer(addressesLayerId)) {
                    mapRef.current.setLayoutProperty(addressesLayerId, "visibility", "visible");
                }
                updateAddressesLayer(
                    mapRef.current,
                    addressesQuery.data as any,
                    flyerTypeId ?? null,
                    true,
                    isDrawingPolygon,
                    selectedAddressIds,
                    campaignFlyerTypes,
                    allCampaignsMode,
                    allCampaignsShowUndelivered,
                    deliveredOnlyView && isCampaignSelected(campaignId),
                    notDeliveredSinceDate,
                    deliveredSinceDate,
                    deliveredUntilDate
                );
                removeAddressAggregationsLayer(mapRef.current);
            } else if (addressesQuery.isError) {
                console.error("[MapView] Addresses query error:", addressesQuery.error);
                // Don't clear on error - keep existing features visible
            } else {
                // data is undefined while loading for a new viewport — keep existing circles visible.
                // Campaign-deselected and isZoomedOut cases are already handled above.
                removeAddressAggregationsLayer(mapRef.current);
            }
        }
    }, [addressesQuery.data, addressesQuery.isError, addressesQuery.error, addressesQuery.isZoomedOut, currentZoom, flyerTypeId, isMapLoaded, isDrawingPolygon, selectedAddressIds, campaignId, hideAllLayers, campaignFlyerTypes, allCampaignsMode, allCampaignsShowUndelivered, notDeliveredSinceDate, deliveredSinceDate, deliveredUntilDate, deliveredOnlyView, mapStyleEpoch]);

    // Note: Address visibility is now handled in updateAddressesLayer to persist during pan/zoom

    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || hideAllLayers) {
            return;
        }

        // Clear assignments layer if hidden or no specific campaign/municipality filter is selected
        if (!showAssignments || (!isCampaignSelected(campaignId) && !municipalityId)) {
            const source = mapRef.current.getSource("assignments");
            if (source && source.type === "geojson") {
                (source as mapboxgl.GeoJSONSource).setData({ type: "FeatureCollection", features: [] });
            }
            return;
        }

        // Note: Don't check isStyleLoaded() here - it can return false temporarily and block layer updates.
        // The isMapLoaded state is sufficient to know the map is ready.

        if (assignmentsQuery.data) {
            updateAssignmentsLayer(mapRef.current, assignmentsQuery.data);
        } else if (assignmentsQuery.isError) {
            console.error("[MapView] Assignments query error:", assignmentsQuery.error);
        } else if (!assignmentsQuery.isLoading && !assignmentsQuery.data) {
            // Clear assignments if query is not loading and no data (campaign changed or disabled)
            const source = mapRef.current.getSource("assignments");
            if (source && source.type === "geojson") {
                (source as mapboxgl.GeoJSONSource).setData({ type: "FeatureCollection", features: [] });
            }
        }
    }, [assignmentsQuery.data, assignmentsQuery.isError, assignmentsQuery.isLoading, assignmentsQuery.error, isMapLoaded, campaignId, municipalityId, showAssignments, hideAllLayers, mapStyleEpoch]);

    useEffect(() => {
        onAssignmentsDataChange?.(assignmentsQuery.data ?? null);
    }, [assignmentsQuery.data, onAssignmentsDataChange]);

    // Distributor pins layer
    const distributorPinsQuery = useKretsMemberLocations(showDistributorPins && isMapLoaded, distributorPinsMunicipalityId);

    useEffect(() => {
        console.log("[MapView] Distributor pins query state", {
            showDistributorPins,
            isMapLoaded,
            distributorPinsMunicipalityId,
            enabled: showDistributorPins && isMapLoaded,
            isLoading: distributorPinsQuery.isLoading,
            isFetching: distributorPinsQuery.isFetching,
            isError: distributorPinsQuery.isError,
            featureCount: distributorPinsQuery.data?.features?.length ?? 0,
            error: distributorPinsQuery.isError ? String(distributorPinsQuery.error) : null
        });
    }, [
        showDistributorPins,
        isMapLoaded,
        distributorPinsMunicipalityId,
        distributorPinsQuery.isLoading,
        distributorPinsQuery.isFetching,
        distributorPinsQuery.isError,
        distributorPinsQuery.error,
        distributorPinsQuery.data
    ]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapLoaded || hideAllLayers) return;
        const features = distributorPinsQuery.data?.features ?? [];
        const mapBounds = map.getBounds();
        if (!mapBounds) return;
        const firstCoordinates = features[0]?.geometry?.coordinates;
        const firstIsInBounds = firstCoordinates
            ? mapBounds.contains([firstCoordinates[0], firstCoordinates[1]])
            : null;

        console.log("[MapView] Distributor pins", {
            visible: showDistributorPins,
            featureCount: features.length,
            municipalityId: distributorPinsMunicipalityId,
            firstCoordinates,
            firstIsInBounds
        });

        // If pins exist but are outside current viewport, move the map to make them visible.
        if (showDistributorPins && features.length > 0) {
            const validCoordinates = features
                .map((f) => f.geometry?.coordinates)
                .filter((coords): coords is [number, number] =>
                    Array.isArray(coords) &&
                    coords.length === 2 &&
                    Number.isFinite(coords[0]) &&
                    Number.isFinite(coords[1])
                );

            if (validCoordinates.length > 0) {
                const pinsInView = validCoordinates.some(([lng, lat]) => mapBounds.contains([lng, lat]));
                if (!pinsInView) {
                    const bounds = new mapboxgl.LngLatBounds(validCoordinates[0], validCoordinates[0]);
                    validCoordinates.slice(1).forEach(([lng, lat]) => bounds.extend([lng, lat]));
                    map.fitBounds(bounds, { padding: 80, maxZoom: 13, duration: 700 });
                    console.log("[MapView] Fitted map to distributor pins", {
                        pinCount: validCoordinates.length
                    });
                }
            }
        }
    }, [distributorPinsQuery.data, showDistributorPins, isMapLoaded, distributorPinsMunicipalityId, hideAllLayers]);

    // Utdelarnas hemadresser som DOM-markörer: oberoende av stil- och lagerordning, och de
    // fungerar även i basemap-läget på /admin/assignments där datalagren är avstängda.
    // (Det fanns tidigare ett symbol-lager som ritade exakt samma punkter — därav dubbla nålar.)
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapLoaded) return;

        // Always clear old markers first.
        distributorMarkersRef.current.forEach((marker) => marker.remove());
        distributorMarkersRef.current = [];

        if (!showDistributorPins) {
            return;
        }

        const features = distributorPinsQuery.data?.features ?? [];
        const markers: mapboxgl.Marker[] = [];

        features.forEach((feature) => {
            const coordinates = feature.geometry?.coordinates;
            if (!coordinates || coordinates.length !== 2) return;
            const [lng, lat] = coordinates;
            if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

            const props = feature.properties;
            // Nålen visar var utdelaren bor, alltså personens namn. Uppdragsnamnen hör hemma
            // på ytorna – och en utdelare med flera uppdrag fick ändå bara ett av dem här.
            const displayName = props?.name?.trim() || "Utdelare";
            const tooltip = [displayName, props?.homeAddress?.trim()].filter(Boolean).join(" · ");

            const wrap = document.createElement("div");
            wrap.style.display = "flex";
            wrap.style.flexDirection = "column";
            wrap.style.alignItems = "center";
            wrap.style.pointerEvents = "auto";

            const label = document.createElement("div");
            label.textContent = displayName;
            label.style.maxWidth = "160px";
            label.style.textAlign = "center";
            label.style.fontSize = "11px";
            label.style.fontWeight = "600";
            label.style.lineHeight = "1.25";
            label.style.color = "#1c1917";
            label.style.textShadow = "0 0 3px #fff, 0 0 6px #fff, 0 0 9px #fff";
            label.style.wordBreak = "break-word";

            const el = document.createElement("div");
            el.style.width = "24px";
            el.style.height = "24px";
            el.style.borderRadius = "999px";
            el.style.background = "#f97316";
            el.style.border = "2px solid #ffffff";
            el.style.boxShadow = "0 0 0 1px rgba(0,0,0,0.2)";
            el.style.display = "flex";
            el.style.alignItems = "center";
            el.style.justifyContent = "center";
            el.style.flexShrink = "0";
            el.innerHTML = `
                <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 11.2L12 4l8 7.2v8.3a.5.5 0 0 1-.5.5h-5.3a.5.5 0 0 1-.5-.5v-4.8h-3.4v4.8a.5.5 0 0 1-.5.5H4.5a.5.5 0 0 1-.5-.5v-8.3z" fill="#ffffff"/>
                </svg>
            `;

            wrap.appendChild(label);
            wrap.appendChild(el);
            wrap.title = tooltip;

            const marker = new mapboxgl.Marker({ element: wrap, anchor: "bottom" })
                .setLngLat([lng, lat])
                .addTo(map);
            markers.push(marker);
        });

        distributorMarkersRef.current = markers;

        return () => {
            markers.forEach((marker) => marker.remove());
            if (distributorMarkersRef.current === markers) {
                distributorMarkersRef.current = [];
            }
        };
    }, [distributorPinsQuery.data, showDistributorPins, isMapLoaded]);

    // Admin /assignments: basemap-only mode — read-only outline + fit bounds (runWhenStyleReady avoids races after setStyle)
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapLoaded) {
            return;
        }

        if (!hideAllLayers) {
            removeAdminAssignmentHighlightFromMap(map);
            removeAdminAssignmentsOverviewFromMap(map);
            return;
        }

        const ring = highlightedAssignmentArea?.coordinates?.[0];
        if (!highlightedAssignmentArea || !ring || ring.length < 4) {
            removeAdminAssignmentHighlightFromMap(map);
            return;
        }

        const areaToDraw = highlightedAssignmentArea;

        return runWhenStyleReady(map, () => {
            applyAdminAssignmentHighlightToMap(map, areaToDraw);
        });
    }, [
        highlightedAssignmentArea,
        assignmentHighlightNonce,
        isMapLoaded,
        hideAllLayers,
        mapStyleEpoch
    ]);

    // Admin /assignments: faint polygons for all custom areas in the municipality (hidden while drawing).
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapLoaded || !hideAllLayers) {
            return;
        }

        const showOverview = !!municipalityId && !isDrawingPolygon;

        return runWhenStyleReady(map, () => {
            updateAdminAssignmentsOverviewLayer(map, adminAssignmentsOverviewFeatures, { show: showOverview });
            updateAdminAssignmentLabelsLayer(map, adminAssignmentPolygonFeatures, { show: showOverview });
        });
    }, [
        hideAllLayers,
        isMapLoaded,
        municipalityId,
        isDrawingPolygon,
        adminAssignmentsOverviewFeatures,
        adminAssignmentPolygonFeatures,
        mapStyleEpoch
    ]);

    // Ensure and update postal codes layer
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded) {
            console.log("[MapView] Skipping postal codes update - map not ready:", {
                hasMap: !!mapRef.current,
                isMapLoaded
            });
            return;
        }

        if (showPostalCodes) {
            console.log("[MapView] Updating postal codes layer:", {
                hasData: !!postalCodesData,
                featureCount: postalCodesData?.features.length ?? 0,
                isStyleLoaded: mapRef.current.isStyleLoaded(),
                dataPreview: postalCodesData ? {
                    firstFeature: postalCodesData.features[0] ? {
                        id: postalCodesData.features[0].id,
                        geometryType: postalCodesData.features[0].geometry?.type,
                        hasCoordinates: !!postalCodesData.features[0].geometry?.coordinates
                    } : null
                } : null
            });

            // Ensure layer exists first, then update
            // Use a helper function that handles both sync and async cases
            const ensureAndUpdate = () => {
                if (!mapRef.current) return;

                // Try to ensure layer (will wait for style.load if needed)
                ensurePostalCodesLayer(mapRef.current)
                    .then(() => {
                        if (!mapRef.current) {
                            console.warn("[MapView] Map ref lost after ensurePostalCodesLayer");
                            return;
                        }

                        const source = mapRef.current.getSource("postal-codes");
                        if (!source || source.type !== "geojson") {
                            console.error("[MapView] Postal codes source not found after ensure!", {
                                hasSource: !!source,
                                sourceType: source?.type,
                                isStyleLoaded: mapRef.current.isStyleLoaded()
                            });
                            // Retry if style wasn't loaded
                            if (!mapRef.current.isStyleLoaded()) {
                                console.log("[MapView] Style not loaded, will retry when style.load fires");
                                mapRef.current.once("style.load", ensureAndUpdate);
                            }
                            return;
                        }

                        console.log("[MapView] Postal codes layer ensured, updating with data");
                        setPostalCodesLayerVisibility(mapRef.current, true);

                        if (postalCodesData && postalCodesData.features.length > 0) {
                            console.log("[MapView] Calling updatePostalCodesLayer with", postalCodesData.features.length, "features");
                            updatePostalCodesLayer(mapRef.current, postalCodesData);

                            // Apply selection highlight right after source data is set (fixes race condition
                            // where the separate selection useEffect runs before .then() resolves)
                            setPostalCodesSelectionHighlight(mapRef.current, selectedPostalCodeIdsRef.current);
                        } else {
                            console.log("[MapView] No postal codes data to display");
                            // Clear postal codes if no data
                            (source as mapboxgl.GeoJSONSource).setData({ type: "FeatureCollection", features: [] });
                        }
                    })
                    .catch((error) => {
                        console.error("[MapView] Failed to ensure postal codes layer:", error);
                    });
            };

            ensureAndUpdate();
        } else {
            console.log("[MapView] Hiding postal codes layer");
            setPostalCodesLayerVisibility(mapRef.current, false);
        }
    }, [showPostalCodes, postalCodesData, isMapLoaded, mapStyleEpoch]);

    // Apply postal code selection highlight when selection or data changes
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || !showPostalCodes || !postalCodesData?.features?.length) return;
        setPostalCodesSelectionHighlight(mapRef.current, selectedPostalCodeIds);
    }, [showPostalCodes, postalCodesData, isMapLoaded, selectedPostalCodeIds, mapStyleEpoch]);

    // Merge electoral districts data with metadata values for choropleth (add metadataValue to feature properties)
    const mergedElectoralDistrictsData = useMemo(() => {
        const data = electoralDistrictsData;
        const values = electoralMetadataValues;
        if (!data?.features?.length) return data ?? null;
        if (!values || !electoralMetadataFieldId) return data;
        const features = data.features.map((feature) => {
            const id = feature.id ?? feature.properties?.electoralDistrictId;
            const districtId = id != null ? String(id) : "";
            const num = values[districtId];
            const properties =
                typeof num === "number" && Number.isFinite(num)
                    ? { ...feature.properties, metadataValue: num }
                    : { ...feature.properties };
            return {
                type: "Feature" as const,
                id: feature.id,
                geometry: feature.geometry,
                properties
            };
        });
        return { ...data, features };
    }, [electoralDistrictsData, electoralMetadataValues, electoralMetadataFieldId]);

    // Ensure and update electoral districts layer (await ensure so data is set after source exists, like postal codes)
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded) {
            return;
        }

        if (showElectoralDistricts) {
            const ensureAndUpdate = () => {
                if (!mapRef.current) return;
                ensureElectoralDistrictsLayer(mapRef.current)
                    .then(() => {
                        if (!mapRef.current) return;
                        const source = mapRef.current.getSource("electoral-districts");
                        if (!source || source.type !== "geojson") {
                            if (!mapRef.current.isStyleLoaded()) {
                                mapRef.current.once("style.load", ensureAndUpdate);
                            }
                            return;
                        }
                        setElectoralDistrictsLayerVisibility(mapRef.current, true);
                        if (mergedElectoralDistrictsData?.features?.length) {
                            updateElectoralDistrictsLayer(mapRef.current, mergedElectoralDistrictsData as import("../api/map/types").ElectoralDistrictsResponse);

                            // Apply selection highlight right after source data is set (fixes race condition
                            // where the separate selection useEffect runs before .then() resolves)
                            setElectoralDistrictsSelectionHighlight(mapRef.current, selectedElectoralDistrictIdsRef.current);
                        } else {
                            (source as mapboxgl.GeoJSONSource).setData({ type: "FeatureCollection", features: [] });
                        }
                        setElectoralDistrictsMetadataPaint(
                            mapRef.current,
                            (electoralMetadataFieldId && electoralMetadataMinMax != null) ? electoralMetadataMinMax : null
                        );
                    })
                    .catch((err) => {
                        console.error("[MapView] Failed to ensure electoral districts layer:", err);
                    });
            };
            ensureAndUpdate();
        } else {
            setElectoralDistrictsLayerVisibility(mapRef.current, false);
        }
    }, [showElectoralDistricts, mergedElectoralDistrictsData, isMapLoaded, electoralMetadataFieldId, electoralMetadataMinMax, mapStyleEpoch]);

    // Apply electoral district selection highlight when selection or data changes
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || !showElectoralDistricts || !mergedElectoralDistrictsData?.features?.length) return;
        setElectoralDistrictsSelectionHighlight(mapRef.current, selectedElectoralDistrictIds);
    }, [showElectoralDistricts, mergedElectoralDistrictsData, isMapLoaded, selectedElectoralDistrictIds, mapStyleEpoch]);

    // Click on postal code polygon toggles selection when click mode is enabled from Kartlager.
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || !onTogglePostalCode || !clickPostalCodesEnabled) return;
        const map = mapRef.current;
        const layerId = "postal-codes-fill";
        const handlePostalClick = (e: mapboxgl.MapMouseEvent) => {
            const features = map.queryRenderedFeatures(e.point, { layers: [layerId] });
            const feature = features[0];
            if (feature?.id != null) {
                onTogglePostalCode(String(feature.id));
            } else if (feature?.properties?.postalCodePolygonId) {
                onTogglePostalCode(String(feature.properties.postalCodePolygonId));
            }
        };
        const handleMouseEnter = () => { map.getCanvas().style.cursor = "pointer"; };
        const handleMouseLeave = () => { map.getCanvas().style.cursor = ""; };
        map.on("click", layerId, handlePostalClick);
        map.on("mouseenter", layerId, handleMouseEnter);
        map.on("mouseleave", layerId, handleMouseLeave);
        return () => {
            map.off("click", layerId, handlePostalClick);
            map.off("mouseenter", layerId, handleMouseEnter);
            map.off("mouseleave", layerId, handleMouseLeave);
        };
    }, [isMapLoaded, onTogglePostalCode, clickPostalCodesEnabled]);

    // Click on electoral district polygon toggles selection when click mode is enabled from Kartlager.
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || !onTogglePriorityElectoralDistrict || !clickElectoralDistrictsEnabled) return;
        const map = mapRef.current;
        const layerId = "electoral-districts-fill";
        const handleElectoralClick = (e: mapboxgl.MapMouseEvent) => {
            const features = map.queryRenderedFeatures(e.point, { layers: [layerId] });
            const feature = features[0];
            if (feature?.id != null) {
                onTogglePriorityElectoralDistrict(String(feature.id));
            } else if (feature?.properties?.electoralDistrictId) {
                onTogglePriorityElectoralDistrict(String(feature.properties.electoralDistrictId));
            }
        };
        const handleMouseEnter = () => { map.getCanvas().style.cursor = "pointer"; };
        const handleMouseLeave = () => { map.getCanvas().style.cursor = ""; };
        map.on("click", layerId, handleElectoralClick);
        map.on("mouseenter", layerId, handleMouseEnter);
        map.on("mouseleave", layerId, handleMouseLeave);
        return () => {
            map.off("click", layerId, handleElectoralClick);
            map.off("mouseenter", layerId, handleMouseEnter);
            map.off("mouseleave", layerId, handleMouseLeave);
        };
    }, [isMapLoaded, onTogglePriorityElectoralDistrict, clickElectoralDistrictsEnabled]);

    // Click on electoral district to show metadata popup when tooltip data is available (e.g. "Visa metadata" checked).
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || !showElectoralDistricts || !electoralDistrictTooltipData) return;
        const map = mapRef.current;
        const layerId = "electoral-districts-fill";
        const escapeHtml = (s: string) =>
            String(s)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;");
        const buildPopupContent = (tooltip: { name: string; fields: { fieldName: string; value: string }[] }) => {
            const title = escapeHtml(tooltip.name);
            const body =
                tooltip.fields.length > 0
                    ? tooltip.fields
                          .map(
                              (f) =>
                                  `<div class="map-view-electoral-tooltip__row"><span class="map-view-electoral-tooltip__label">${escapeHtml(f.fieldName)}:</span> ${escapeHtml(f.value)}</div>`
                          )
                          .join("")
                    : "<div class=\"map-view-electoral-tooltip__empty\">Inga siffror</div>";
            return `<div class="map-view-electoral-tooltip__inner" role="dialog" aria-label="${escapeHtml(tooltip.name)}"><div class="map-view-electoral-tooltip__title">${title}</div>${body}</div>`;
        };
        const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
            const features = map.queryRenderedFeatures(e.point, { layers: [layerId] });
            const feature = features[0];
            const districtId =
                feature?.id != null
                    ? String(feature.id)
                    : (feature?.properties as { electoralDistrictId?: string } | undefined)?.electoralDistrictId;
            if (!districtId) {
                electoralTooltipPopupRef.current?.remove();
                electoralTooltipPopupRef.current = null;
                electoralTooltipDistrictIdRef.current = null;
                return;
            }
            const tooltip = electoralDistrictTooltipData[districtId];
            if (!tooltip) {
                electoralTooltipPopupRef.current?.remove();
                electoralTooltipPopupRef.current = null;
                electoralTooltipDistrictIdRef.current = null;
                return;
            }
            if (electoralTooltipDistrictIdRef.current === districtId) {
                electoralTooltipPopupRef.current?.remove();
                electoralTooltipPopupRef.current = null;
                electoralTooltipDistrictIdRef.current = null;
                return;
            }
            electoralTooltipDistrictIdRef.current = districtId;
            electoralTooltipPopupRef.current?.remove();
            const popup = new mapboxgl.Popup({
                closeButton: true,
                closeOnClick: true,
                className: "map-view-electoral-tooltip"
            })
                .setLngLat(e.lngLat)
                .setHTML(buildPopupContent(tooltip))
                .addTo(map);
            electoralTooltipPopupRef.current = popup;
        };
        map.on("click", handleMapClick);
        return () => {
            map.off("click", handleMapClick);
            electoralTooltipPopupRef.current?.remove();
            electoralTooltipPopupRef.current = null;
            electoralTooltipDistrictIdRef.current = null;
        };
    }, [isMapLoaded, showElectoralDistricts, electoralDistrictTooltipData]);

    // Auto-center/zoom when fitBounds changes (e.g. municipality or saved set loaded)
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || !fitBounds) return;
        const parsed = parseBoundingBox(fitBounds);
        if (!parsed) return;
        try {
            mapRef.current.fitBounds(
                [
                    [parsed.minLongitude, parsed.minLatitude],
                    [parsed.maxLongitude, parsed.maxLatitude]
                ],
                { padding: 40, maxZoom: 14, duration: 800 }
            );
        } catch (error) {
            console.warn("[MapView] fitBounds failed:", error);
        }
    }, [isMapLoaded, fitBounds]);

    // Ensure and update municipalities layer (async ensure like postal codes)
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded) {
            return;
        }

        if (showMunicipalities) {
            ensureMunicipalitiesLayer(mapRef.current)
                .then(() => {
                    if (!mapRef.current) return;
                    setMunicipalitiesLayerVisibility(mapRef.current, true);
                    if (municipalitiesData?.features?.length) {
                        updateMunicipalitiesLayer(mapRef.current, municipalitiesData);
                    } else {
                        const source = mapRef.current.getSource("municipalities");
                        if (source && source.type === "geojson") {
                            (source as mapboxgl.GeoJSONSource).setData({ type: "FeatureCollection", features: [] });
                        }
                    }
                    updateMunicipalityHighlight(mapRef.current, selectedMunicipalityFeature ?? null);
                })
                .catch((error) => {
                    console.error("[MapView] Failed to ensure municipalities layer:", error);
                });
        } else {
            setMunicipalitiesLayerVisibility(mapRef.current, false);
        }
    }, [showMunicipalities, municipalitiesData, isMapLoaded, selectedMunicipalityFeature, mapStyleEpoch]);

    // Update municipality highlight when selection changes (ram runt vald kommun)
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded) return;
        ensureMunicipalitiesLayer(mapRef.current)
            .then(() => {
                if (!mapRef.current) return;
                updateMunicipalityHighlight(mapRef.current, selectedMunicipalityFeature ?? null);
            })
            .catch((error) => {
                console.error("[MapView] Failed to ensure municipality highlight layer:", error);
            });
    }, [selectedMunicipalityFeature, isMapLoaded, mapStyleEpoch]);

    // Update addresses layer styling when flyer filter changes (only when map is loaded)
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || hideAllLayers) {
            return;
        }

        ensureAddressesLayer({
            map: mapRef.current,
            selectedFlyerTypeId: flyerTypeId ?? null,
            selectedAddressIds
        });
    }, [flyerTypeId, isMapLoaded, selectedAddressIds, hideAllLayers, mapStyleEpoch]);

    // Highlight road segment when bulk delivery dialog is open
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded) {
            return;
        }

        if (bulkDeliveryDialog) {
            highlightRoadSegment(mapRef.current, bulkDeliveryDialog.roadSegmentId);
        } else {
            removeRoadSegmentHighlight(mapRef.current);
        }

        return () => {
            if (mapRef.current && isMapLoaded) {
                removeRoadSegmentHighlight(mapRef.current);
            }
        };
    }, [bulkDeliveryDialog, isMapLoaded, mapStyleEpoch]);

    // Add click handler for roads layer to trigger bulk delivery
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || !isCampaignSelected(campaignId) || isDrawingPolygon) {
            return;
        }

        const map = mapRef.current;
        const layerId = "roads-line";

        const handleRoadClick = (e: mapboxgl.MapMouseEvent) => {
            if (!e.features || e.features.length === 0) {
                return;
            }

            const feature = e.features[0];
            const properties = feature.properties as RoadFeatureProperties;

            if (!properties.roadSegmentId) {
                return;
            }

            // Fetch actual address count using preview endpoint with larger buffer for better coverage
            if (campaignId) {
                previewAddressCountByRoad(campaignId, properties.roadSegmentId, 40.0, 80.0)
                    .then((count) => {
                        setBulkDeliveryDialog({
                            roadSegmentId: properties.roadSegmentId,
                            roadName: properties.refName ?? undefined,
                            estimatedAddressCount: count
                        });
                    })
                    .catch((error) => {
                        console.error("Failed to preview address count:", error);
                        // Fallback to default estimate
                        setBulkDeliveryDialog({
                            roadSegmentId: properties.roadSegmentId,
                            roadName: properties.refName ?? undefined,
                            estimatedAddressCount: 50
                        });
                    });
            } else {
                // Fallback if no campaignId
                setBulkDeliveryDialog({
                    roadSegmentId: properties.roadSegmentId,
                    roadName: properties.refName ?? undefined,
                    estimatedAddressCount: 50
                });
            }

            // Prevent default map behavior
            e.preventDefault();
        };

        map.on("click", layerId, handleRoadClick);

        // Change cursor on hover
        const handleMouseEnter = () => {
            map.getCanvas().style.cursor = "pointer";
        };

        const handleMouseLeave = () => {
            map.getCanvas().style.cursor = "";
        };

        map.on("mouseenter", layerId, handleMouseEnter);
        map.on("mouseleave", layerId, handleMouseLeave);

        return () => {
            map.off("click", layerId, handleRoadClick);
            map.off("mouseenter", layerId, handleMouseEnter);
            map.off("mouseleave", layerId, handleMouseLeave);
        };
    }, [mapRef.current, isMapLoaded, campaignId, isDrawingPolygon]);

    // Address layer: left-click selects the address and opens the bulk panel (single selection).
    // Bulk multi-select: Ctrl/Cmd/Shift + click, or a primary touch pointer (mobile web).
    // Right-click (context menu) opens the floating AddressContextMenu.
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || isCreateMode || isDrawingPolygon) {
            return;
        }

        const map = mapRef.current;
        const layerId = "addresses-points";

        const addressFromFeature = (feature: GeoJSON.Feature): AddressResponse | null => {
            const properties = feature.properties as AddressFeatureProperties;
            const addressPointId = properties.addressPointId;
            if (!addressPointId) {
                return null;
            }
            return {
                id: addressPointId,
                street: properties.street,
                number: properties.number ?? null,
                apartment: properties.apartment ?? null,
                city: properties.city,
                postalCode: properties.postalCode,
                latitude: (feature.geometry as GeoJSON.Point).coordinates[1],
                longitude: (feature.geometry as GeoJSON.Point).coordinates[0],
                isBlocked: properties.isBlocked,
                isHidden: properties.isHidden,
                deliveryUnitCount: properties.deliveryUnitCount,
                createdAt: properties.createdAt,
                updatedAt: properties.updatedAt ?? null
            };
        };

        // Hidden addresses are rendered fully transparent below HIDDEN_VISIBLE_FROM_ZOOM but
        // remain hit-testable in Mapbox, so clicks on them must be ignored there too.
        const firstInteractiveFeature = (features: GeoJSON.Feature[] | undefined): GeoJSON.Feature | null => {
            if (!features || features.length === 0) {
                return null;
            }
            if (map.getZoom() >= HIDDEN_VISIBLE_FROM_ZOOM) {
                return features[0];
            }
            return features.find((f) => !(f.properties as AddressFeatureProperties | null)?.isHidden) ?? null;
        };

        const shouldToggleBulkSelection = (e: mapboxgl.MapMouseEvent): boolean => {
            const raw = e.originalEvent;
            if (typeof PointerEvent !== "undefined" && raw instanceof PointerEvent) {
                if (raw.pointerType === "touch") {
                    return true;
                }
                return raw.ctrlKey || raw.metaKey || raw.shiftKey;
            }
            if (raw instanceof MouseEvent) {
                return raw.ctrlKey || raw.metaKey || raw.shiftKey;
            }
            return false;
        };

        const handleAddressClick = (e: mapboxgl.MapMouseEvent) => {
            const feature = firstInteractiveFeature(e.features);
            if (!feature) {
                return;
            }

            const addressPointId = feature.properties?.addressPointId as string | undefined;

            if (!addressPointId) {
                return;
            }

            e.preventDefault();

            if (allCampaignsMode) {
                setSelectedAddressIds([addressPointId]);
                return;
            }

            if (shouldToggleBulkSelection(e)) {
                setSelectedAddressIds((prev) => {
                    const index = prev.indexOf(addressPointId);
                    if (index >= 0) {
                        return prev.filter((id) => id !== addressPointId);
                    }
                    return [...prev, addressPointId];
                });
                return;
            }

            setSelectedAddressIds([addressPointId]);
        };

        const handleAddressRightClick = (e: mapboxgl.MapMouseEvent) => {
            const feature = firstInteractiveFeature(e.features);
            if (!feature) {
                return;
            }

            e.preventDefault();

            const address = addressFromFeature(feature);
            if (!address) {
                return;
            }

            setContextMenu({
                address,
                position: { x: e.point.x, y: e.point.y }
            });
        };

        map.on("click", layerId, handleAddressClick);
        map.on("contextmenu", layerId, handleAddressRightClick);

        // Change cursor on hover
        const handleMouseEnter = () => {
            map.getCanvas().style.cursor = "pointer";
        };

        const handleMouseLeave = () => {
            map.getCanvas().style.cursor = "";
        };

        map.on("mouseenter", layerId, handleMouseEnter);
        map.on("mouseleave", layerId, handleMouseLeave);

        return () => {
            map.off("click", layerId, handleAddressClick);
            map.off("contextmenu", layerId, handleAddressRightClick);
            map.off("mouseenter", layerId, handleMouseEnter);
            map.off("mouseleave", layerId, handleMouseLeave);
        };
    }, [mapRef.current, isMapLoaded, isCreateMode, isDrawingPolygon, allCampaignsMode]);

    // Handle map click in create mode
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || !isCreateMode || isDrawingPolygon) {
            return;
        }

        const map = mapRef.current;

        const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
            setCreatePopover({
                position: { x: e.point.x, y: e.point.y },
                latitude: e.lngLat.lat,
                longitude: e.lngLat.lng
            });
        };

        map.on("click", handleMapClick);
        map.getCanvas().style.cursor = "crosshair";

        return () => {
            map.off("click", handleMapClick);
            map.getCanvas().style.cursor = "";
        };
    }, [mapRef.current, isMapLoaded, isCreateMode, isDrawingPolygon]);

    // Poster mode: manage layer lifecycle + hide/show address/road layers
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded) return;
        const map = mapRef.current;
        const addressLayerIds = ["addresses-points", "roads-line"];
        if (mapMode === "poster") {
            ensurePostersLayer(map);
            setPostersShowTakenDown(map, showTakenDownPosters);
            for (const id of addressLayerIds) {
                if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", "none");
            }
        } else {
            setPosterContextMenu(null);
            setPosterPlaceDialog(null);
            removePostersLayer(map);
            for (const id of addressLayerIds) {
                if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", "visible");
            }
        }
    }, [mapMode, isMapLoaded, mapStyleEpoch, showTakenDownPosters]);

    // Poster mode: fetch GeoJSON when the viewport changes. Affischlagret är medvetet
    // *inte* kampanjfiltrerat – affischer sätts upp på den kampanj som råkar vara vald,
    // men de ska synas på kartan oavsett vilken kampanj man tittar på just nu.
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || mapMode !== "poster" || !viewportBbox) return;
        const map = mapRef.current;

        posterFetchAbortRef.current?.abort();
        const controller = new AbortController();
        posterFetchAbortRef.current = controller;

        fetchPostersGeoJson(null, viewportBbox, controller.signal)
            .then((data) => {
                if (map.getSource("posters")) {
                    updatePostersLayer(map, data);
                }
            })
            .catch(() => {/* aborted or network error */});

        return () => { controller.abort(); };
    }, [mapMode, isMapLoaded, viewportBbox, campaignId]);

    // Poster mode: click on poster pin
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || mapMode !== "poster") return;
        const map = mapRef.current;

        const handlePosterClick = (e: mapboxgl.MapMouseEvent) => {
            if (!e.features || e.features.length === 0) return;
            const feature = e.features[0];
            const props = feature.properties as PosterFeatureProperties;
            const id = feature.id as string | undefined;
            if (!id || !props) return;
            posterPinClickedRef.current = true;
            Promise.resolve().then(() => { posterPinClickedRef.current = false; });
            setPostersSelectedId(map, id);
            setPosterContextMenu({ poster: { ...props, id }, position: { x: e.point.x, y: e.point.y } });
            setPosterPlaceDialog(null);
        };

        const handleMouseEnterPoster = () => { map.getCanvas().style.cursor = "pointer"; };
        const handleMouseLeavePoster = () => { map.getCanvas().style.cursor = ""; };

        map.on("click", POSTERS_CLICKABLE_LAYER_ID, handlePosterClick);
        map.on("mouseenter", POSTERS_CLICKABLE_LAYER_ID, handleMouseEnterPoster);
        map.on("mouseleave", POSTERS_CLICKABLE_LAYER_ID, handleMouseLeavePoster);

        return () => {
            map.off("click", POSTERS_CLICKABLE_LAYER_ID, handlePosterClick);
            map.off("mouseenter", POSTERS_CLICKABLE_LAYER_ID, handleMouseEnterPoster);
            map.off("mouseleave", POSTERS_CLICKABLE_LAYER_ID, handleMouseLeavePoster);
        };
    }, [mapMode, isMapLoaded, mapStyleEpoch]);

    // Poster mode: click on empty map to place poster
    useEffect(() => {
        if (!mapRef.current || !isMapLoaded || mapMode !== "poster" || !campaignId) return;
        const map = mapRef.current;

        const handleMapClickForPoster = (e: mapboxgl.MapMouseEvent) => {
            if (mapModeRef.current !== "poster") return;
            if (posterPinClickedRef.current) return;
            setPosterContextMenu(null);
            setPostersSelectedId(map, null);
            setPosterPlaceDialog({
                position: { x: e.point.x, y: e.point.y },
                latitude: e.lngLat.lat,
                longitude: e.lngLat.lng
            });
        };

        map.on("click", handleMapClickForPoster);
        map.getCanvas().style.cursor = "crosshair";

        return () => {
            map.off("click", handleMapClickForPoster);
            map.getCanvas().style.cursor = "";
        };
    }, [mapMode, isMapLoaded, campaignId]);

    // Get latest metadata for status indicator
    const latestMetadata = addressesQuery.data?.metadata?.generatedAt;

    const handleRefresh = () => {
        queryClient.invalidateQueries({ queryKey: ["map"] });
    };

    if (errorKey) {
        return (
            <div className="map-view__fallback" role="alert">
                <p>{t(errorKey)}</p>
                <p>{t("map.errors.helpText")}</p>
            </div>
        );
    }

    const isRefetching = useMemo(() =>
        roadsQuery.isFetching ||
        addressesQuery.isFetching ||
        assignmentsQuery.isFetching,
        [roadsQuery.isFetching, addressesQuery.isFetching, assignmentsQuery.isFetching]
    );

    const isLoading = useMemo(() =>
        (roadsQuery.isLoading || addressesQuery.isLoading || assignmentsQuery.isLoading) &&
        !roadsQuery.data &&
        !addressesQuery.data &&
        !assignmentsQuery.data,
        [roadsQuery.isLoading, addressesQuery.isLoading, assignmentsQuery.isLoading,
         roadsQuery.data, addressesQuery.data, assignmentsQuery.data]
    );

    // Collect errors from queries - only show errors if we don't have cached data
    // If we have placeholderData (cached data), don't show the error
    const errors = [
        { key: "roads", error: roadsQuery.error, query: roadsQuery },
        { key: "addresses", error: addressesQuery.error, query: addressesQuery },
        { key: "assignments", error: assignmentsQuery.error, query: assignmentsQuery }
    ].filter((e) => {
        // Don't show error if:
        // 1. No error
        // 2. Error is dismissed
        // 3. We have cached data (placeholderData) - this means the query is using previous data
        return e.error && !dismissedErrors.has(e.key) && !e.query.data;
    });

    const hasCachedData =
        (roadsQuery.data || addressesQuery.data || assignmentsQuery.data) &&
        errors.length > 0;

    const handleRetry = (queryKey: string) => {
        setDismissedErrors((prev) => {
            const next = new Set(prev);
            next.delete(queryKey);
            return next;
        });
        queryClient.invalidateQueries({ queryKey: ["map", queryKey] });
    };

    const handleDismissError = (queryKey: string) => {
        setDismissedErrors((prev) => new Set(prev).add(queryKey));
    };

    const handleUseCached = () => {
        // Dismiss all errors and use cached data
        errors.forEach((e) => setDismissedErrors((prev) => new Set(prev).add(e.key)));
    };

    const handleBulkDeliveryConfirm = (bufferM: number, flyerTypeIds: string[]) => {
        if (!bulkDeliveryDialog || !isCampaignSelected(campaignId)) {
            return;
        }

        if (flyerTypeIds.length === 0) {
            // Error should be shown in dialog
            return;
        }

        // Note: For bulk delivery by road, we can't know which addresses will be delivered
        // ahead of time, so we can't do optimistic updates here.
        // The addresses will turn green when the API response comes back and queries are refetched.
        recordDeliveriesByRoadMutation.mutate(
            {
                campaignId: campaignId!,
                roadSegmentId: bulkDeliveryDialog.roadSegmentId,
                flyerTypeIds,
                bufferM,
                relaxedStreetBufferM: bufferM * 2.5, // Use relaxed buffer (2.5x) for same street name
                clientRequestId: getCurrentSessionId()
            },
            {
                onSuccess: () => {
                    setBulkDeliveryDialog(null);
                    // Query refetch is handled by the mutation hook for instant visual feedback
                },
                onError: () => {
                    // Keep dialog open on error so user can retry
                }
            }
        );
    };

    const handleBulkDeliveryCancel = () => {
        setBulkDeliveryDialog(null);
    };

    const boxSelectEnabled =
        isBoxSelectMode &&
        isMapLoaded &&
        !isDrawingPolygon &&
        !hideAllLayers &&
        !allCampaignsMode &&
        mapMode !== "poster" &&
        !isCreateMode &&
        isCampaignSelected(campaignId);

    useBoxSelect({
        map: (isMapLoaded ? (contextMap || mapRef.current) : null),
        enabled: boxSelectEnabled,
        onSelection: (ids) => {
            setSelectedAddressIds(ids);
            setIsBoxSelectMode(false);
        }
    });

    // The map's button column, laid out two per row.
    const showCreateAddressButton = !isDrawingPolygon && !hideAllLayers;
    const showBoxSelectButton =
        isCampaignSelected(campaignId) &&
        !isDrawingPolygon &&
        !hideAllLayers &&
        mapMode !== "poster" &&
        !allCampaignsMode;

    return (
        <div className="map-view-wrapper">
            <div ref={containerRef} className="map-view" data-testid="map-view" />

            {/* PolygonDrawer stays mounted whenever the map exists so the [enabled] effect
                fires correctly on enable/disable transitions and cleans up properly */}
            {(contextMap || mapRef.current) && (
                <PolygonDrawer
                    map={contextMap || mapRef.current}
                    initialArea={polygonArea || undefined}
                    onAreaChange={setPolygonArea}
                    enabled={isDrawingPolygon}
                />
            )}

            {/* Loading Skeleton */}
            {isLoading && (
                <MapLoadingSkeleton
                    isLoading={isLoading}
                    message={
                        !isOnline
                            ? t("map.loading.offline")
                            : undefined
                    }
                />
            )}

            {/* Error Alerts */}
            {errors.length > 0 && (
                <div className="map-view__overlay map-view__overlay--top-center">
                    {errors.map(({ key, error }) => (
                        <MapErrorAlert
                            key={key}
                            error={error ?? null}
                            onRetry={() => handleRetry(key)}
                            onDismiss={() => handleDismissError(key)}
                            showFallback={hasCachedData}
                            onUseCached={errors.length > 1 ? handleUseCached : undefined}
                        />
                    ))}
                </div>
            )}

            {/* Offline Indicator */}
            {!isOnline && (
                <div className="map-view__overlay map-view__overlay--top-center">
                    <div className="map-offline-indicator" role="status" aria-live="polite">
                        <span className="map-offline-indicator__icon" aria-hidden="true">
                            📡
                        </span>
                        <span>{t("map.offline.message")}</span>
                    </div>
                </div>
            )}

            {/* Overlay UI Components */}
            <div className="map-view__overlays">
                {showFilterPanel && (
                    <div className="map-view__overlay map-view__overlay--top-left">
                        <MapFilterPanel
                            campaigns={campaigns}
                            flyerTypes={flyerTypes}
                            selectedCampaignId={campaignId}
                            selectedFlyerTypeId={flyerTypeId}
                            onCampaignChange={onCampaignChange}
                            onFlyerTypeChange={onFlyerTypeChange}
                            allCampaignsShowUndelivered={allCampaignsShowUndelivered}
                            onAllCampaignsShowUndeliveredChange={(show) => {
                                setAllCampaignsShowUndelivered(show);
                                if (!show) {
                                    setNotDeliveredSinceDate(null);
                                }
                            }}
                            campaignShowUndelivered={campaignShowUndelivered}
                            onCampaignShowUndeliveredChange={setCampaignShowUndelivered}
                            campaignUndeliveredForcedOff={
                                isCampaignSelected(campaignId) && currentZoom < AGGREGATION_ZOOM_THRESHOLD
                            }
                            notDeliveredSinceDate={notDeliveredSinceDate}
                            onNotDeliveredSinceDateChange={(date) => {
                                setNotDeliveredSinceDate(date);
                                if (date) {
                                    setDeliveredSinceDate(null);
                                    setDeliveredUntilDate(null);
                                }
                            }}
                            deliveredSinceDate={deliveredSinceDate}
                            onDeliveredSinceDateChange={(date) => {
                                setDeliveredSinceDate(date);
                                if (date) {
                                    setNotDeliveredSinceDate(null);
                                } else {
                                    setDeliveredUntilDate(null);
                                }
                            }}
                            deliveredUntilDate={deliveredUntilDate}
                            onDeliveredUntilDateChange={setDeliveredUntilDate}
                            householdStats={householdStats}
                            isLoading={isRefetching}
                            mapMode={mapMode}
                            onMapModeChange={campaignId ? setMapMode : undefined}
                        />
                    </div>
                )}

                {showStatusIndicator && (
                    <div className="map-view__overlay map-view__overlay--top-right">
                        <MapStatusIndicator
                            lastUpdated={latestMetadata}
                            isRefetching={isRefetching}
                            onRefresh={handleRefresh}
                        />
                    </div>
                )}

                <div className="map-view__overlay map-view__overlay--base-map">
                    {/* Row 1: what the map shows. Row 2: what a click on it does. */}
                    <div className="map-view__map-button-row">
                        <button
                            type="button"
                            className="map-view__base-map-toggle"
                            onClick={handleBaseMapToggle}
                            disabled={!isMapLoaded || isBaseMapSwitching}
                            title={
                                useSatelliteImagery
                                    ? t("map.baseMap.toggleStandard")
                                    : t("map.baseMap.toggleSatellite")
                            }
                        >
                            {useSatelliteImagery ? t("map.baseMap.standard") : t("map.baseMap.satellite")}
                        </button>
                        {showCreateAddressButton && (
                            <button
                                type="button"
                                className={`map-view__create-address-button ${isCreateMode ? "active" : ""}`}
                                onClick={() => {
                                    setIsCreateMode(!isCreateMode);
                                    if (isCreateMode) {
                                        setCreatePopover(null);
                                    }
                                }}
                                aria-pressed={isCreateMode}
                            >
                                {isCreateMode
                                    ? t("map.createAddress.cancel", "Avbryt")
                                    : t("map.createAddress.toggle", "Ny adress")}
                            </button>
                        )}
                    </div>
                    {showBoxSelectButton && (
                        <div className="map-view__map-button-row">
                            <button
                                type="button"
                                className={`map-view__box-select-toggle${isBoxSelectMode ? " map-view__box-select-toggle--active" : ""}`}
                                onClick={() => setIsBoxSelectMode((prev) => !prev)}
                                disabled={!isMapLoaded}
                                title={t("map.boxSelect.toggleHint")}
                                aria-pressed={isBoxSelectMode}
                            >
                                {t("map.boxSelect.toggle")}
                            </button>
                        </div>
                    )}
                </div>

                {showLegend && (
                    <div className="map-view__overlay map-view__overlay--bottom-left">
                        <MapLegend
                            showAddresses={true}
                            showAssignments={true}
                            showRoads={false}
                        />
                    </div>
                )}

                {/* Zoom Level Indicator - Hide when drawing polygon for assignments or in admin assignment mode */}
                {!isDrawingPolygon && !hideAllLayers && (
                    <div className="map-view__overlay map-view__overlay--bottom-right">
                        <MapZoomIndicator
                            zoom={currentZoom}
                            aggregationThreshold={AGGREGATION_ZOOM_THRESHOLD}
                            showAggregationInfo={true}
                            modeLabel={deliveredOnlyView ? "Delivered only" : "Individual points"}
                        />
                    </div>
                )}
            </div>

            {/* Status Bar - Bottom of map (goals when logged in; poster stats in poster mode) */}
            {mapMode === "poster" && isCampaignSelected(campaignId) ? (
                <PosterStatusBar
                    showTakenDown={showTakenDownPosters}
                    onShowTakenDownChange={setShowTakenDownPosters}
                />
            ) : (
                showStatusBar && (hasMapCampaignScope(campaignId) || currentUser) && (
                    <MapStatusBar
                        campaignId={isCampaignSelected(campaignId) ? campaignId : undefined}
                        onShowRoReporting={isCampaignSelected(campaignId) ? () => setShowRoReportingModal(true) : undefined}
                        onShowMissedAddresses={isCampaignSelected(campaignId) ? () => setShowMissedAddressesModal(true) : undefined}
                    />
                )
            )}

            {/* RO Reporting Modal */}
            {showRoReportingModal && isCampaignSelected(campaignId) && (
                <RoReportingModal
                    campaignId={campaignId!}
                    onClose={() => setShowRoReportingModal(false)}
                />
            )}

            {/* Missed Addresses Review Modal */}
            {showMissedAddressesModal && isCampaignSelected(campaignId) && (
                <MissedAddressesModal
                    campaignId={campaignId!}
                    flyerTypeIds={flyerTypeId ? [flyerTypeId] : campaignFlyerTypes.map(ft => ft?.id).filter((id): id is string => id !== undefined)}
                    onClose={() => setShowMissedAddressesModal(false)}
                />
            )}

            {/* Bulk Delivery Confirmation Dialog */}
            {bulkDeliveryDialog && (
                <BulkDeliveryConfirmationDialog
                    roadSegmentId={bulkDeliveryDialog.roadSegmentId}
                    roadName={bulkDeliveryDialog.roadName}
                    estimatedAddressCount={bulkDeliveryDialog.estimatedAddressCount}
                    bufferM={40.0}
                    flyerTypes={campaignFlyerTypes}
                    selectedFlyerTypeIds={flyerTypeId ? [flyerTypeId] : campaignFlyerTypes.map(ft => ft?.id).filter((id): id is string => id !== undefined)}
                    onConfirm={handleBulkDeliveryConfirm}
                    onCancel={handleBulkDeliveryCancel}
                    isLoading={recordDeliveriesByRoadMutation.isPending}
                />
            )}

            {/* Bulk Address Delivery Panel */}
            {hasMapCampaignScope(campaignId) && selectedAddressIds.length > 0 && (!allCampaignsMode || selectedAddressIds.length === 1) && (
                <BulkAddressDeliveryPanel
                    campaignId={isCampaignSelected(campaignId) ? campaignId! : ""}
                    deliveryActionsEnabled={!allCampaignsMode}
                    selectedAddressIds={selectedAddressIds}
                    flyerTypeIds={flyerTypeId ? [flyerTypeId] : []}
                    map={mapRef.current}
                    onClearSelection={() => setSelectedAddressIds([])}
                    singleAddress={singleAddress}
                    selectedFlyerTypeId={flyerTypeId}
                    deliveries={(singleAddressDeliveriesQuery.data as DeliveryResponse[] | undefined) ?? []}
                    currentUserId={currentUser?.id}
                    onUndoDelivery={
                        !allCampaignsMode && campaignId && flyerTypeId && singleAddress && singleAddressDeliveriesQuery.data && Array.isArray(singleAddressDeliveriesQuery.data) && singleAddressDeliveriesQuery.data.length > 0
                            ? async () => {
                                // Capture data before panel closes
                                const addressId = singleAddress.id;
                                const currentFlyerTypeId = flyerTypeId;

                                try {
                                    const deliveries = singleAddressDeliveriesQuery.data as DeliveryResponse[];
                                    // Find all deliveries for the selected flyer type that the current user created
                                    const deliveriesToDelete = deliveries.filter(
                                        (d: DeliveryResponse) =>
                                            deliveryMatchesSelectedFlyerType(d, currentFlyerTypeId) &&
                                            !d.isNoThanks &&
                                            (d.quantity ?? 0) > 0 &&
                                            (!currentUser || d.recordedByUserId === currentUser.id)
                                    );

                                    if (deliveriesToDelete.length === 0) {
                                        toast.error(t("map.addressContextMenu.noDeliveriesToUndo"));
                                        return;
                                    }

                                    // Immediate visual feedback directly on the Mapbox source (same pattern as hide/block)
                                    if (mapRef.current) {
                                        markAddressAsUndelivered(mapRef.current, addressId, currentFlyerTypeId, flyerTypeId ?? null, campaignFlyerTypes);
                                    }

                                    // Optimistic React Query cache update
                                    await queryClient.cancelQueries({ queryKey: ["map", "addresses", "deliveries"] });
                                    removeAddressFlyerDeliveriesFromMapCache(queryClient, String(addressId), currentFlyerTypeId);

                                    // Panel closes via onClearSelection() in BulkAddressDeliveryPanel

                                    // Delete all deliveries for this flyer type
                                    await Promise.all(
                                        deliveriesToDelete.map((d: DeliveryResponse) => deleteDeliveryMutation.mutateAsync(d.id))
                                    );

                                    toast.success(t("map.addressContextMenu.undoDeliverySuccess"));
                                    // Background sync (no await)
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                } catch (error) {
                                    // On error, force a full refetch to restore the correct visual state
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                                    toast.error(errorMessage);
                                }
                            }
                            : undefined
                    }
                    onHide={
                        singleAddress
                            ? async () => {
                                // Capture values before panel closes via onClearSelection
                                const addressId = singleAddress.id;
                                const wasHidden = singleAddress.isHidden;
                                const newIsHidden = !wasHidden;

                                // Update the map immediately for instant visual feedback
                                if (mapRef.current) {
                                    updateAddressStatus(
                                        mapRef.current,
                                        addressId,
                                        undefined, // isBlocked - keep current
                                        newIsHidden, // isHidden - update
                                        flyerTypeId ?? undefined
                                    );
                                }

                                // Panel closes via onClearSelection() in BulkAddressDeliveryPanel

                                try {
                                    await updateAddress.mutateAsync({
                                        id: addressId,
                                        request: {
                                            isHidden: newIsHidden,
                                            ...(newIsHidden && isCampaignSelected(campaignId)
                                                ? { campaignId: campaignId! }
                                                : {})
                                        }
                                    });
                                    toast.success(
                                        wasHidden
                                            ? t("map.addressContextMenu.showSuccess")
                                            : t("map.addressContextMenu.hideSuccess")
                                    );
                                } catch (error) {
                                    // Revert the optimistic update on error
                                    if (mapRef.current) {
                                        updateAddressStatus(
                                            mapRef.current,
                                            addressId,
                                            undefined, // isBlocked - keep current
                                            wasHidden, // isHidden - revert
                                            flyerTypeId ?? undefined
                                        );
                                    }
                                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                                    toast.error(errorMessage);
                                }
                            }
                            : undefined
                    }
                    onBlock={
                        singleAddress
                            ? async () => {
                                // Capture values before panel closes via onClearSelection
                                const addressId = singleAddress.id;
                                const wasBlocked = singleAddress.isBlocked;
                                const newIsBlocked = !wasBlocked;

                                // Update the map immediately for instant visual feedback
                                if (mapRef.current) {
                                    updateAddressStatus(
                                        mapRef.current,
                                        addressId,
                                        newIsBlocked, // isBlocked - update
                                        undefined, // isHidden - keep current
                                        flyerTypeId ?? undefined
                                    );
                                }

                                // Panel closes via onClearSelection() in BulkAddressDeliveryPanel

                                try {
                                    await updateAddress.mutateAsync({
                                        id: addressId,
                                        request: { isBlocked: newIsBlocked }
                                    });
                                    toast.success(
                                        wasBlocked
                                            ? t("map.addressContextMenu.unblockSuccess")
                                            : t("map.addressContextMenu.blockSuccess")
                                    );
                                } catch (error) {
                                    // Revert the optimistic update on error
                                    if (mapRef.current) {
                                        updateAddressStatus(
                                            mapRef.current,
                                            addressId,
                                            wasBlocked, // isBlocked - revert
                                            undefined, // isHidden - keep current
                                            flyerTypeId ?? undefined
                                        );
                                    }
                                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                                    toast.error(errorMessage);
                                }
                            }
                            : undefined
                    }
                    onMove={
                        singleAddress
                            ? () => {
                                movePoint.startMove(
                                    singleAddress.id,
                                    singleAddress.latitude,
                                    singleAddress.longitude
                                );
                                // Panel closes via onClearSelection() in BulkAddressDeliveryPanel
                            }
                            : undefined
                    }
                    onEdit={
                        singleAddress
                            ? () => {
                                setEditDialogAddressId(singleAddress.id);
                                // Panel closes via onClearSelection() in BulkAddressDeliveryPanel
                            }
                            : undefined
                    }
                    onCopy={
                        singleAddress
                            ? async () => {
                                // Capture address data before panel closes
                                const originalAddress = singleAddress;

                                // Construct the address name: "Gata 1B" -> "Gata 1B Copy"
                                // Append " Copy" to the number if it exists, otherwise to the street
                                let copyStreet = originalAddress.street;
                                let copyNumber = originalAddress.number;

                                if (copyNumber) {
                                    copyNumber = `${copyNumber} Copy`;
                                } else {
                                    copyStreet = `${copyStreet} Copy`;
                                }

                                // Panel closes via onClearSelection() in BulkAddressDeliveryPanel

                                try {
                                    await createAddress.mutateAsync({
                                        latitude: originalAddress.latitude,
                                        longitude: originalAddress.longitude,
                                        street: copyStreet,
                                        number: copyNumber,
                                        apartment: originalAddress.apartment,
                                        city: originalAddress.city,
                                        postalCode: originalAddress.postalCode
                                    });

                                    toast.success(t("map.addressContextMenu.copySuccess"));
                                } catch (error) {
                                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                                    toast.error(errorMessage);
                                }
                            }
                            : undefined
                    }
                    onMarkNoThanks={
                        !allCampaignsMode && isCampaignSelected(campaignId) && flyerTypeId && singleAddress
                            ? async () => {
                                const addressIdToMark = singleAddress.id;
                                try {
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, addressIdToMark, "delivered");
                                    }

                                    await queryClient.cancelQueries({ queryKey: ["map", "addresses", "deliveries"] });
                                    addDeliveryToMapCache(queryClient, addressIdToMark, flyerTypeId);
                                    if (mapRef.current && addressesQuery.data) {
                                        const _data = addressesQuery.data as any;
                                        const _modifiedFeatures = _data.features.map((f: any) => {
                                            if (f.properties.addressPointId !== addressIdToMark) return f;
                                            const existing: any[] = f.properties.flyerStatuses ?? [];
                                            const idx = existing.findIndex(
                                                (s: any) => s.flyerTypeId === flyerTypeId || s.campaignFlyerId === flyerTypeId
                                            );
                                            const nextStatus = {
                                                addressPointId: f.properties.addressPointId,
                                                campaignId: "",
                                                flyerTypeId: flyerTypeId,
                                                campaignFlyerId: null,
                                                distributorAssignmentId: null,
                                                deliveredQuantity: 0,
                                                isNoThanks: true,
                                                lastDeliveredAt: new Date().toISOString(),
                                                recordedById: ""
                                            };
                                            const flyerStatuses =
                                                idx >= 0
                                                    ? existing.map((s: any, i: number) => (i === idx ? { ...s, ...nextStatus } : s))
                                                    : [...existing, nextStatus];
                                            return { ...f, properties: { ...f.properties, flyerStatuses } };
                                        });
                                        updateAddressesLayer(mapRef.current, { ..._data, features: _modifiedFeatures }, flyerTypeId ?? null, true, isDrawingPolygon, selectedAddressIds, campaignFlyerTypes);
                                    }

                                    await recordDeliveriesByAddressesMutation.mutateAsync({
                                        campaignId: campaignId!,
                                        addressPointIds: [addressIdToMark],
                                        flyerTypeIds: [flyerTypeId],
                                        clientRequestId: getCurrentSessionId(),
                                        isNoThanks: true
                                    });
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, addressIdToMark, null);
                                    }
                                    toast.success(t("map.addressContextMenu.markNoThanksSuccess"));
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                } catch (error) {
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, addressIdToMark, null);
                                    }
                                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                                    toast.error(errorMessage);
                                }
                            }
                            : undefined
                    }
                    onConvertToNoThanks={
                        !allCampaignsMode && isCampaignSelected(campaignId) && flyerTypeId && singleAddress && singleAddressDeliveriesQuery.data && Array.isArray(singleAddressDeliveriesQuery.data)
                            ? async () => {
                                const addressId = singleAddress.id;
                                const currentFlyerTypeId = flyerTypeId;

                                try {
                                    const deliveries = singleAddressDeliveriesQuery.data as DeliveryResponse[];
                                    const deliveriesToDelete = deliveries.filter(
                                        (d: DeliveryResponse) =>
                                            deliveryMatchesSelectedFlyerType(d, currentFlyerTypeId) &&
                                            !d.isNoThanks &&
                                            (d.quantity ?? 0) > 0 &&
                                            (!currentUser || d.recordedByUserId === currentUser.id)
                                    );

                                    if (deliveriesToDelete.length === 0) {
                                        toast.error(t("map.addressContextMenu.noDeliveriesToUndo"));
                                        return;
                                    }

                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), "delivered");
                                    }

                                    await queryClient.cancelQueries({ queryKey: ["map", "addresses", "deliveries"] });
                                    removeAddressFlyerDeliveriesFromMapCache(queryClient, String(addressId), currentFlyerTypeId);
                                    if (mapRef.current && addressesQuery.data) {
                                        const _data = addressesQuery.data as any;
                                        const _addrId = String(addressId);
                                        const _modifiedFeatures = _data.features.map((f: any) => {
                                            if (f.properties.addressPointId !== _addrId) return f;
                                            const existing: any[] = f.properties.flyerStatuses ?? [];
                                            const idx = existing.findIndex(
                                                (s: any) => s.flyerTypeId === currentFlyerTypeId || s.campaignFlyerId === currentFlyerTypeId
                                            );
                                            const nextStatus = {
                                                addressPointId: f.properties.addressPointId,
                                                campaignId: "",
                                                flyerTypeId: currentFlyerTypeId,
                                                campaignFlyerId: null,
                                                distributorAssignmentId: null,
                                                deliveredQuantity: 0,
                                                isNoThanks: true,
                                                lastDeliveredAt: new Date().toISOString(),
                                                recordedById: ""
                                            };
                                            const flyerStatuses =
                                                idx >= 0
                                                    ? existing.map((s: any, i: number) => (i === idx ? { ...s, ...nextStatus } : s))
                                                    : [...existing, nextStatus];
                                            return { ...f, properties: { ...f.properties, flyerStatuses } };
                                        });
                                        updateAddressesLayer(mapRef.current, { ..._data, features: _modifiedFeatures }, flyerTypeId ?? null, true, isDrawingPolygon, selectedAddressIds, campaignFlyerTypes);
                                    }

                                    await Promise.all(
                                        deliveriesToDelete.map((d: DeliveryResponse) => deleteDeliveryMutation.mutateAsync(d.id))
                                    );
                                    await recordDeliveriesByAddressesMutation.mutateAsync({
                                        campaignId: campaignId!,
                                        addressPointIds: [addressId],
                                        flyerTypeIds: [currentFlyerTypeId],
                                        clientRequestId: getCurrentSessionId(),
                                        isNoThanks: true
                                    });

                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), null);
                                    }
                                    toast.success(t("map.addressContextMenu.convertToNoThanksSuccess"));
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                } catch (error) {
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), null);
                                    }
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                                    toast.error(errorMessage);
                                }
                            }
                            : undefined
                    }
                    onUndoNoThanks={
                        !allCampaignsMode && isCampaignSelected(campaignId) && flyerTypeId && singleAddress && singleAddressDeliveriesQuery.data && Array.isArray(singleAddressDeliveriesQuery.data) && singleAddressDeliveriesQuery.data.length > 0
                            ? async () => {
                                const addressId = singleAddress.id;
                                const currentFlyerTypeId = flyerTypeId;

                                try {
                                    const deliveries = singleAddressDeliveriesQuery.data as DeliveryResponse[];
                                    const deliveriesToDelete = deliveries.filter(
                                        (d: DeliveryResponse) =>
                                            deliveryMatchesSelectedFlyerType(d, currentFlyerTypeId) &&
                                            d.isNoThanks &&
                                            (!currentUser || d.recordedByUserId === currentUser.id)
                                    );

                                    if (deliveriesToDelete.length === 0) {
                                        toast.error(t("map.addressContextMenu.noDeliveriesToUndo"));
                                        return;
                                    }

                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), "pending");
                                    }

                                    await queryClient.cancelQueries({ queryKey: ["map", "addresses", "deliveries"] });
                                    removeAddressFlyerDeliveriesFromMapCache(queryClient, String(addressId), currentFlyerTypeId);
                                    if (mapRef.current && addressesQuery.data) {
                                        const _data = addressesQuery.data as any;
                                        const _addrId = String(addressId);
                                        const _ftId = currentFlyerTypeId;
                                        const _modifiedFeatures = _data.features.map((f: any) => {
                                            if (f.properties.addressPointId !== _addrId) return f;
                                            const filtered = (f.properties.flyerStatuses ?? []).filter(
                                                (s: any) => s.flyerTypeId !== _ftId && s.campaignFlyerId !== _ftId
                                            );
                                            return { ...f, properties: { ...f.properties, flyerStatuses: filtered } };
                                        });
                                        updateAddressesLayer(mapRef.current, { ..._data, features: _modifiedFeatures }, flyerTypeId ?? null, true, isDrawingPolygon, selectedAddressIds, campaignFlyerTypes);
                                    }

                                    await Promise.all(
                                        deliveriesToDelete.map((d: DeliveryResponse) => deleteDeliveryMutation.mutateAsync(d.id))
                                    );

                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), null);
                                    }
                                    toast.success(t("map.addressContextMenu.undoNoThanksSuccess"));
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                } catch (error) {
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), null);
                                    }
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                                    toast.error(errorMessage);
                                }
                            }
                            : undefined
                    }
                    onDeliveryHistory={
                        singleAddress ? () => setDeliveryHistoryAddress(singleAddress) : undefined
                    }
                />
            )}

            {/* Address Context Menu */}
            {contextMenu && (
                <AddressContextMenu
                    address={contextMenu.address}
                    position={contextMenu.position}
                    onClose={() => setContextMenu(null)}
                    onMarkDelivered={
                        isCampaignSelected(campaignId)
                            ? async () => {
                                const addressIdToMark = contextMenu.address.id;
                                try {
                                    // Use selected flyer type if available, otherwise use all flyer types for the campaign
                                    const flyerTypeIdsToUse = flyerTypeId
                                        ? [flyerTypeId]
                                        : campaignFlyerTypes.map((ft) => ft?.id).filter((id): id is string => id !== undefined);

                                    if (flyerTypeIdsToUse.length === 0) {
                                        toast.error(t("map.addressContextMenu.noFlyerTypes"));
                                        return;
                                    }

                                    // Immediate visual feedback via feature-state (like hide/block)
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, addressIdToMark, "delivered");
                                    }

                                    // Optimistic update: patch RQ cache + directly update the layer.
                                    await queryClient.cancelQueries({ queryKey: ["map", "addresses", "deliveries"] });
                                    for (const ftId of flyerTypeIdsToUse) {
                                        addDeliveryToMapCache(queryClient, addressIdToMark, ftId);
                                    }
                                    if (mapRef.current && addressesQuery.data) {
                                        const _data = addressesQuery.data as any;
                                        const _modifiedFeatures = _data.features.map((f: any) => {
                                            if (f.properties.addressPointId !== addressIdToMark) return f;
                                            const existing: any[] = f.properties.flyerStatuses ?? [];
                                            const added = flyerTypeIdsToUse
                                                .filter(ftId => !existing.some((s: any) => s.flyerTypeId === ftId || s.campaignFlyerId === ftId))
                                                .map(ftId => ({ addressPointId: f.properties.addressPointId, campaignId: "", flyerTypeId: ftId, campaignFlyerId: null, distributorAssignmentId: null, deliveredQuantity: 1, lastDeliveredAt: new Date().toISOString(), recordedById: "" }));
                                            return { ...f, properties: { ...f.properties, flyerStatuses: [...existing, ...added] } };
                                        });
                                        updateAddressesLayer(mapRef.current, { ..._data, features: _modifiedFeatures }, flyerTypeId ?? null, true, isDrawingPolygon, selectedAddressIds, campaignFlyerTypes);
                                    }

                                    // Close menu immediately after optimistic update
                                    setContextMenu(null);

                                    await recordDeliveriesByAddressesMutation.mutateAsync({
                                        campaignId: campaignId!,
                                        addressPointIds: [addressIdToMark],
                                        flyerTypeIds: flyerTypeIdsToUse,
                                        clientRequestId: getCurrentSessionId()
                                    });
                                    // Clear override — GeoJSON data already reflects "delivered" from optimistic update
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, addressIdToMark, null);
                                    }
                                    toast.success(t("map.addressContextMenu.markDeliveredSuccess"));
                                    // Background sync to replace optimistic placeholder with real server data
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                } catch (error) {
                                    // Revert optimistic visual update on error
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, addressIdToMark, null);
                                    }
                                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                                    toast.error(errorMessage);
                                }
                            }
                            : undefined
                    }
                    onMarkNoThanks={
                        isCampaignSelected(campaignId) && flyerTypeId
                            ? async () => {
                                const addressIdToMark = contextMenu.address.id;
                                try {
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, addressIdToMark, "delivered");
                                    }

                                    await queryClient.cancelQueries({ queryKey: ["map", "addresses", "deliveries"] });
                                    addDeliveryToMapCache(queryClient, addressIdToMark, flyerTypeId);
                                    if (mapRef.current && addressesQuery.data) {
                                        const _data = addressesQuery.data as any;
                                        const _modifiedFeatures = _data.features.map((f: any) => {
                                            if (f.properties.addressPointId !== addressIdToMark) return f;
                                            const existing: any[] = f.properties.flyerStatuses ?? [];
                                            const idx = existing.findIndex(
                                                (s: any) => s.flyerTypeId === flyerTypeId || s.campaignFlyerId === flyerTypeId
                                            );
                                            const nextStatus = {
                                                addressPointId: f.properties.addressPointId,
                                                campaignId: "",
                                                flyerTypeId: flyerTypeId,
                                                campaignFlyerId: null,
                                                distributorAssignmentId: null,
                                                deliveredQuantity: 0,
                                                isNoThanks: true,
                                                lastDeliveredAt: new Date().toISOString(),
                                                recordedById: ""
                                            };
                                            const flyerStatuses =
                                                idx >= 0
                                                    ? existing.map((s: any, i: number) => (i === idx ? { ...s, ...nextStatus } : s))
                                                    : [...existing, nextStatus];
                                            return { ...f, properties: { ...f.properties, flyerStatuses } };
                                        });
                                        updateAddressesLayer(mapRef.current, { ..._data, features: _modifiedFeatures }, flyerTypeId ?? null, true, isDrawingPolygon, selectedAddressIds, campaignFlyerTypes);
                                    }

                                    setContextMenu(null);

                                    await recordDeliveriesByAddressesMutation.mutateAsync({
                                        campaignId: campaignId!,
                                        addressPointIds: [addressIdToMark],
                                        flyerTypeIds: [flyerTypeId],
                                        clientRequestId: getCurrentSessionId(),
                                        isNoThanks: true
                                    });
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, addressIdToMark, null);
                                    }
                                    toast.success(t("map.addressContextMenu.markNoThanksSuccess"));
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                } catch (error) {
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, addressIdToMark, null);
                                    }
                                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                                    toast.error(errorMessage);
                                }
                            }
                            : undefined
                    }
                    onConvertToNoThanks={
                        isCampaignSelected(campaignId) && flyerTypeId && deliveriesQuery.data && Array.isArray(deliveriesQuery.data)
                            ? async () => {
                                const addressId = contextMenu.address.id;
                                const currentFlyerTypeId = flyerTypeId;

                                try {
                                    const deliveries = deliveriesQuery.data as DeliveryResponse[];
                                    const deliveriesToDelete = deliveries.filter(
                                        (d: DeliveryResponse) =>
                                            deliveryMatchesSelectedFlyerType(d, currentFlyerTypeId) &&
                                            !d.isNoThanks &&
                                            (d.quantity ?? 0) > 0 &&
                                            (!currentUser || d.recordedByUserId === currentUser.id)
                                    );

                                    if (deliveriesToDelete.length === 0) {
                                        toast.error(t("map.addressContextMenu.noDeliveriesToUndo"));
                                        return;
                                    }

                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), "delivered");
                                    }

                                    await queryClient.cancelQueries({ queryKey: ["map", "addresses", "deliveries"] });
                                    removeAddressFlyerDeliveriesFromMapCache(queryClient, String(addressId), currentFlyerTypeId);
                                    if (mapRef.current && addressesQuery.data) {
                                        const _data = addressesQuery.data as any;
                                        const _addrId = String(addressId);
                                        const _modifiedFeatures = _data.features.map((f: any) => {
                                            if (f.properties.addressPointId !== _addrId) return f;
                                            const existing: any[] = f.properties.flyerStatuses ?? [];
                                            const idx = existing.findIndex(
                                                (s: any) => s.flyerTypeId === currentFlyerTypeId || s.campaignFlyerId === currentFlyerTypeId
                                            );
                                            const nextStatus = {
                                                addressPointId: f.properties.addressPointId,
                                                campaignId: "",
                                                flyerTypeId: currentFlyerTypeId,
                                                campaignFlyerId: null,
                                                distributorAssignmentId: null,
                                                deliveredQuantity: 0,
                                                isNoThanks: true,
                                                lastDeliveredAt: new Date().toISOString(),
                                                recordedById: ""
                                            };
                                            const flyerStatuses =
                                                idx >= 0
                                                    ? existing.map((s: any, i: number) => (i === idx ? { ...s, ...nextStatus } : s))
                                                    : [...existing, nextStatus];
                                            return { ...f, properties: { ...f.properties, flyerStatuses } };
                                        });
                                        updateAddressesLayer(mapRef.current, { ..._data, features: _modifiedFeatures }, flyerTypeId ?? null, true, isDrawingPolygon, selectedAddressIds, campaignFlyerTypes);
                                    }

                                    setContextMenu(null);

                                    await Promise.all(
                                        deliveriesToDelete.map((d: DeliveryResponse) => deleteDeliveryMutation.mutateAsync(d.id))
                                    );
                                    await recordDeliveriesByAddressesMutation.mutateAsync({
                                        campaignId: campaignId!,
                                        addressPointIds: [addressId],
                                        flyerTypeIds: [currentFlyerTypeId],
                                        clientRequestId: getCurrentSessionId(),
                                        isNoThanks: true
                                    });

                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), null);
                                    }
                                    toast.success(t("map.addressContextMenu.convertToNoThanksSuccess"));
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                } catch (error) {
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), null);
                                    }
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                                    toast.error(errorMessage);
                                }
                            }
                            : undefined
                    }
                    onUndoDelivery={
                        isCampaignSelected(campaignId) && flyerTypeId && deliveriesQuery.data && Array.isArray(deliveriesQuery.data) && deliveriesQuery.data.length > 0
                            ? async () => {
                                // Capture data before menu closes
                                const addressId = contextMenu.address.id;
                                const currentFlyerTypeId = flyerTypeId;

                                try {
                                    const deliveries = deliveriesQuery.data as DeliveryResponse[];
                                    // Find all deliveries for the selected flyer type that the current user created
                                    const deliveriesToDelete = deliveries.filter(
                                        (d: DeliveryResponse) =>
                                            deliveryMatchesSelectedFlyerType(d, currentFlyerTypeId) &&
                                            !d.isNoThanks &&
                                            (d.quantity ?? 0) > 0 &&
                                            (!currentUser || d.recordedByUserId === currentUser.id)
                                    );

                                    if (deliveriesToDelete.length === 0) {
                                        toast.error(t("map.addressContextMenu.noDeliveriesToUndo"));
                                        return;
                                    }

                                    // Immediate visual feedback via feature-state (like hide/block)
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), "pending");
                                    }

                                    // Optimistic update: patch RQ cache + directly update the layer.
                                    await queryClient.cancelQueries({ queryKey: ["map", "addresses", "deliveries"] });
                                    removeAddressFlyerDeliveriesFromMapCache(queryClient, String(addressId), currentFlyerTypeId);
                                    if (mapRef.current && addressesQuery.data) {
                                        const _data = addressesQuery.data as any;
                                        const _addrId = String(addressId);
                                        const _ftId = currentFlyerTypeId;
                                        const _modifiedFeatures = _data.features.map((f: any) => {
                                            if (f.properties.addressPointId !== _addrId) return f;
                                            const filtered = (f.properties.flyerStatuses ?? []).filter(
                                                (s: any) => s.flyerTypeId !== _ftId && s.campaignFlyerId !== _ftId
                                            );
                                            return { ...f, properties: { ...f.properties, flyerStatuses: filtered } };
                                        });
                                        updateAddressesLayer(mapRef.current, { ..._data, features: _modifiedFeatures }, flyerTypeId ?? null, true, isDrawingPolygon, selectedAddressIds, campaignFlyerTypes);
                                    }

                                    // Menu closes via onClose() in AddressContextMenu

                                    // Delete all deliveries for this flyer type
                                    await Promise.all(
                                        deliveriesToDelete.map((d: DeliveryResponse) => deleteDeliveryMutation.mutateAsync(d.id))
                                    );

                                    // GeoJSON data already shows "pending" from the optimistic layer update above;
                                    // clear the feature-state override immediately so the circle is correct without
                                    // waiting for a background network round-trip.
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), null);
                                    }
                                    toast.success(t("map.addressContextMenu.undoDeliverySuccess"));
                                    // Background sync (no await — don't block the UI)
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                } catch (error) {
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), null);
                                    }
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                                    toast.error(errorMessage);
                                }
                            }
                            : undefined
                    }
                    onUndoNoThanks={
                        isCampaignSelected(campaignId) && flyerTypeId && deliveriesQuery.data && Array.isArray(deliveriesQuery.data) && deliveriesQuery.data.length > 0
                            ? async () => {
                                const addressId = contextMenu.address.id;
                                const currentFlyerTypeId = flyerTypeId;

                                try {
                                    const deliveries = deliveriesQuery.data as DeliveryResponse[];
                                    const deliveriesToDelete = deliveries.filter(
                                        (d: DeliveryResponse) =>
                                            deliveryMatchesSelectedFlyerType(d, currentFlyerTypeId) &&
                                            d.isNoThanks &&
                                            (!currentUser || d.recordedByUserId === currentUser.id)
                                    );

                                    if (deliveriesToDelete.length === 0) {
                                        toast.error(t("map.addressContextMenu.noDeliveriesToUndo"));
                                        return;
                                    }

                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), "pending");
                                    }

                                    await queryClient.cancelQueries({ queryKey: ["map", "addresses", "deliveries"] });
                                    removeAddressFlyerDeliveriesFromMapCache(queryClient, String(addressId), currentFlyerTypeId);
                                    if (mapRef.current && addressesQuery.data) {
                                        const _data = addressesQuery.data as any;
                                        const _addrId = String(addressId);
                                        const _ftId = currentFlyerTypeId;
                                        const _modifiedFeatures = _data.features.map((f: any) => {
                                            if (f.properties.addressPointId !== _addrId) return f;
                                            const filtered = (f.properties.flyerStatuses ?? []).filter(
                                                (s: any) => s.flyerTypeId !== _ftId && s.campaignFlyerId !== _ftId
                                            );
                                            return { ...f, properties: { ...f.properties, flyerStatuses: filtered } };
                                        });
                                        updateAddressesLayer(mapRef.current, { ..._data, features: _modifiedFeatures }, flyerTypeId ?? null, true, isDrawingPolygon, selectedAddressIds, campaignFlyerTypes);
                                    }

                                    await Promise.all(
                                        deliveriesToDelete.map((d: DeliveryResponse) => deleteDeliveryMutation.mutateAsync(d.id))
                                    );

                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), null);
                                    }
                                    toast.success(t("map.addressContextMenu.undoNoThanksSuccess"));
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                } catch (error) {
                                    if (mapRef.current) {
                                        setAddressDeliveryOverride(mapRef.current, String(addressId), null);
                                    }
                                    requestMapDeliveriesFullFetch();
                                    queryClient.invalidateQueries({
                                        queryKey: ["map", "addresses", "deliveries"],
                                        refetchType: "active"
                                    });
                                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                                    toast.error(errorMessage);
                                }
                            }
                            : undefined
                    }
                    selectedFlyerTypeId={flyerTypeId}
                    deliveries={(contextMenuDeliveries as DeliveryResponse[] | undefined) ?? []}
                    currentUserId={currentUser?.id}
                    onHide={async () => {
                        // Capture address data before menu closes
                        const address = contextMenu.address;
                        const addressId = address.id;
                        const wasHidden = address.isHidden;
                        const newIsHidden = !wasHidden;

                        // Update the map immediately for instant visual feedback
                        if (mapRef.current) {
                            updateAddressStatus(
                                mapRef.current,
                                addressId,
                                undefined, // isBlocked - keep current
                                newIsHidden, // isHidden - update
                                flyerTypeId ?? undefined
                            );
                        }

                        // Menu closes via onClose() in AddressContextMenu - don't need to close here

                        try {
                            await updateAddress.mutateAsync({
                                id: addressId,
                                request: {
                                    isHidden: newIsHidden,
                                    ...(newIsHidden && isCampaignSelected(campaignId) ? { campaignId: campaignId! } : {})
                                }
                            });
                            toast.success(
                                wasHidden
                                    ? t("map.addressContextMenu.showSuccess")
                                    : t("map.addressContextMenu.hideSuccess")
                            );
                        } catch (error) {
                            // Revert the optimistic update on error
                            if (mapRef.current) {
                                updateAddressStatus(
                                    mapRef.current,
                                    addressId,
                                    undefined, // isBlocked - keep current
                                    wasHidden, // isHidden - revert
                                    flyerTypeId ?? undefined
                                );
                            }
                            const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                            toast.error(errorMessage);
                        }
                    }}
                    onBlock={async () => {
                        // Capture address data before menu closes
                        const address = contextMenu.address;
                        const addressId = address.id;
                        const wasBlocked = address.isBlocked;
                        const newIsBlocked = !wasBlocked;

                        // Update the map immediately for instant visual feedback
                        if (mapRef.current) {
                            updateAddressStatus(
                                mapRef.current,
                                addressId,
                                newIsBlocked, // isBlocked - update
                                undefined, // isHidden - keep current
                                flyerTypeId ?? undefined
                            );
                        }

                        // Menu closes via onClose() in AddressContextMenu - don't need to close here

                        try {
                            await updateAddress.mutateAsync({
                                id: addressId,
                                request: { isBlocked: newIsBlocked }
                            });
                            toast.success(
                                wasBlocked
                                    ? t("map.addressContextMenu.unblockSuccess")
                                    : t("map.addressContextMenu.blockSuccess")
                            );
                        } catch (error) {
                            // Revert the optimistic update on error
                            if (mapRef.current) {
                                updateAddressStatus(
                                    mapRef.current,
                                    addressId,
                                    wasBlocked, // isBlocked - revert
                                    undefined, // isHidden - keep current
                                    flyerTypeId ?? undefined
                                );
                            }
                            const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                            toast.error(errorMessage);
                        }
                    }}
                    onMove={() => {
                        // Capture address data before menu closes
                        const address = contextMenu.address;
                        movePoint.startMove(
                            address.id,
                            address.latitude,
                            address.longitude
                        );
                        // Menu closes via onClose() in AddressContextMenu
                    }}
                    onEdit={() => {
                        // Capture address ID before menu closes
                        const addressId = contextMenu.address.id;
                        setEditDialogAddressId(addressId);
                        // Menu closes via onClose() in AddressContextMenu
                    }}
                    onCopy={async () => {
                        // Capture address data before menu closes
                        const originalAddress = contextMenu.address;

                        // Construct the address name: "Gata 1B" -> "Gata 1B Copy"
                        // Append " Copy" to the number if it exists, otherwise to the street
                        let copyStreet = originalAddress.street;
                        let copyNumber = originalAddress.number;

                        if (copyNumber) {
                            copyNumber = `${copyNumber} Copy`;
                        } else {
                            copyStreet = `${copyStreet} Copy`;
                        }

                        // Menu closes via onClose() in AddressContextMenu

                        try {
                            await createAddress.mutateAsync({
                                latitude: originalAddress.latitude,
                                longitude: originalAddress.longitude,
                                street: copyStreet,
                                number: copyNumber,
                                apartment: originalAddress.apartment,
                                city: originalAddress.city,
                                postalCode: originalAddress.postalCode
                            });

                            toast.success(t("map.addressContextMenu.copySuccess"));
                        } catch (error) {
                            const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                            toast.error(errorMessage);
                        }
                    }}
                    onDeliveryHistory={() => {
                        setDeliveryHistoryAddress(contextMenu.address);
                        setContextMenu(null);
                    }}
                />
            )}

            <AddressDeliveryHistoryModal
                open={deliveryHistoryAddress !== null}
                address={deliveryHistoryAddress}
                onClose={() => setDeliveryHistoryAddress(null)}
            />

            {/* Address Edit Dialog */}
            {editDialogAddressId && editAddress && (
                <AddressEditDialog
                    address={editAddress}
                    open={!!editDialogAddressId}
                    onClose={() => setEditDialogAddressId(null)}
                    onSave={async (request) => {
                        await updateAddress.mutateAsync({
                            id: editDialogAddressId,
                            request: {
                                ...request,
                                // Campaign scope lets the server re-sync quantities on already-recorded
                                // deliveries when the unit count changes.
                                campaignId: isCampaignSelected(campaignId) ? campaignId : undefined
                            }
                        });
                    }}
                    isLoading={updateAddress.isPending}
                />
            )}

            {/* Create Address Popover - Hide when drawing polygon for assignments or in admin assignment mode */}
            {!isDrawingPolygon && !hideAllLayers && createPopover && (
                <CreateAddressPopover
                    position={createPopover.position}
                    latitude={createPopover.latitude}
                    longitude={createPopover.longitude}
                    onClose={() => {
                        setCreatePopover(null);
                        setIsCreateMode(false);
                    }}
                    onCreate={async (request) => {
                        await createAddress.mutateAsync(request);
                    }}
                    isLoading={createAddress.isPending}
                />
            )}

            {/* Poster Context Menu */}
            {posterContextMenu && (
                <PosterContextMenu
                    poster={posterContextMenu.poster}
                    position={posterContextMenu.position}
                    isLoading={isPosterActionLoading}
                    onClose={() => {
                        setPosterContextMenu(null);
                        if (mapRef.current) setPostersSelectedId(mapRef.current, null);
                    }}
                    onUndo={posterContextMenu.poster.placedByMe && posterContextMenu.poster.status === "Active" ? async () => {
                        if (!campaignId) return;
                        setIsPosterActionLoading(true);
                        try {
                            await apiDeletePoster(posterContextMenu.poster.id);
                            if (mapRef.current) {
                                const data = await fetchPostersGeoJson(null, viewportBbox);
                                updatePostersLayer(mapRef.current, data);
                            }
                            queryClient.invalidateQueries({ queryKey: ["posters", "statistics"] });
                        } finally {
                            setIsPosterActionLoading(false);
                            setPosterContextMenu(null);
                            if (mapRef.current) setPostersSelectedId(mapRef.current, null);
                        }
                    } : undefined}
                    onTakeDown={posterContextMenu.poster.status === "Active" ? async () => {
                        setIsPosterActionLoading(true);
                        try {
                            await apiUpdatePosterStatus(posterContextMenu.poster.id, "TakenDown");
                            if (mapRef.current) {
                                const data = await fetchPostersGeoJson(null, viewportBbox);
                                updatePostersLayer(mapRef.current, data);
                            }
                            queryClient.invalidateQueries({ queryKey: ["posters", "statistics"] });
                        } finally {
                            setIsPosterActionLoading(false);
                            setPosterContextMenu(null);
                            if (mapRef.current) setPostersSelectedId(mapRef.current, null);
                        }
                    } : undefined}
                    onVandalized={posterContextMenu.poster.status !== "Vandalized" ? async () => {
                        setIsPosterActionLoading(true);
                        try {
                            await apiUpdatePosterStatus(posterContextMenu.poster.id, "Vandalized");
                            if (mapRef.current) {
                                const data = await fetchPostersGeoJson(null, viewportBbox);
                                updatePostersLayer(mapRef.current, data);
                            }
                            queryClient.invalidateQueries({ queryKey: ["posters", "statistics"] });
                        } finally {
                            setIsPosterActionLoading(false);
                            setPosterContextMenu(null);
                            if (mapRef.current) setPostersSelectedId(mapRef.current, null);
                        }
                    } : undefined}
                />
            )}

            {/* Poster Place Dialog */}
            {posterPlaceDialog && campaignId && (
                <PosterPlaceDialog
                    position={posterPlaceDialog.position}
                    isLoading={isPosterActionLoading}
                    onClose={() => setPosterPlaceDialog(null)}
                    onPlace={async (quantity) => {
                        setIsPosterActionLoading(true);
                        try {
                            await apiPlacePoster({
                                campaignId,
                                latitude: posterPlaceDialog.latitude,
                                longitude: posterPlaceDialog.longitude,
                                quantity
                            });
                            setPosterPlaceDialog(null);
                            if (mapRef.current) {
                                const data = await fetchPostersGeoJson(null, viewportBbox);
                                updatePostersLayer(mapRef.current, data);
                            }
                            queryClient.invalidateQueries({ queryKey: ["posters", "statistics"] });
                        } finally {
                            setIsPosterActionLoading(false);
                        }
                    }}
                />
            )}

            {/* Move Address Confirmation Panel */}
            {movePoint.isMoving && (
                <div className="map-view__overlay map-view__overlay--top-center">
                    <div className="map-view__move-confirmation-panel">
                        <div className="map-view__move-confirmation-message">
                            {t("map.moveAddress.instructions")}
                        </div>
                        <div className="map-view__move-confirmation-actions">
                            <button
                                type="button"
                                className="map-view__move-confirmation-button map-view__move-confirmation-button--cancel"
                                onClick={() => movePoint.cancelMove()}
                            >
                                {t("common.cancel")}
                            </button>
                            <button
                                type="button"
                                className="map-view__move-confirmation-button map-view__move-confirmation-button--confirm"
                                onClick={() => {
                                    if (movePoint.markerRef.current) {
                                        const lngLat = movePoint.markerRef.current.getLngLat();
                                        void movePoint.confirmMove(lngLat.lat, lngLat.lng);
                                    }
                                }}
                            >
                                {t("map.moveAddress.confirm")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};